/**
 * Every string Studio shows, in one place (English for now). Keeping
 * copy out of components makes localizing Studio with Glossa itself a
 * mechanical change later: these keys become messages.
 */
import { ApiError } from "./api/errors";
import { auditProblems, auditStrings } from "./strings-audit";
import { directoryProblems, directoryStrings } from "./strings-directory";
import { instanceProblems, instanceStrings } from "./strings-instances";
import { releaseOpsProblems, releaseOpsStrings } from "./strings-release-ops";
import { workflowProblems, workflowStrings } from "./strings-workflows";

export const strings = {
  app: {
    name: "Glossa Studio",
    beta: "Public Beta",
    skip: "Skip to content",
    loading: "Loading…",
    retry: "Try again",
    cancel: "Cancel",
    close: "Close",
    save: "Save",
    saving: "Saving…",
    remove: "Remove",
    edit: "Edit",
    delete: "Delete",
    notFound: "This page doesn't exist.",
    backHome: "Back to projects",
  },
  nav: {
    projects: "Projects",
    translate: "Translate",
    locales: "Locales",
    settings: "Settings",
    releases: "Releases",
    review: "Review",
    termbase: "Termbase",
    style: "Style",
    ai: "AI",
    quality: "Quality",
    files: "Import & export",
    shortcuts: "Keyboard shortcuts",
    account: "Account & security",
    signOut: "Sign out",
    signOutEverywhere: "Sign out everywhere",
    tenant: "Workspace",
    individual: "Personal",
    organization: "Organization",
    newOrganization: "New organization…",
    breadcrumb: "Breadcrumb",
    projectSections: "Project sections",
    workspaceSettings: "Workspace settings",
    work: "Your work",
    myWork: "My work",
    approvals: "Approvals",
    workflow: "Workflow",
    workflows: "Workflows",
    groups: "Groups",
    vendors: "Vendors",
    audit: "Audit log",
  },
  tenantSettings: {
    knowledgeLink: "Translation memory & termbase",
    githubLink: "GitHub",
    settingsLead: "Settings of the whole workspace, shared by every project.",
    github: {
      title: "GitHub",
      lead: "Connect the Glossa app to your GitHub account, then tie each repository to the project and application whose messages it builds. Glossa never reads your code: it watches pull requests, reports its checks on them, and leaves everything else to your CI.",
      back: "Projects",
      notConfigured: "This Glossa deployment has no GitHub app configured, so there is nothing to connect. Ask whoever runs it to register one.",
      noManageRights: "Connecting GitHub needs the manage permission (developers, admins, owners). You can see what is connected.",

      connectTitle: "Connected accounts",
      connectLead: "Installing the app on an account lets Glossa see which repositories it may act on. You will be sent to GitHub to choose the account and the repositories, and back here to finish.",
      connect: "Connect a GitHub account",
      connecting: "Sending you to GitHub…",
      finishing: "Finishing the installation…",
      connected: (account: string) => `${account} is connected.`,
      noInstallations: "No GitHub account is connected yet.",
      account: "Account",
      repositories: "Repositories",
      installState: {
        active: "Connected",
        suspended: "Suspended on GitHub",
        revoked: "Uninstalled on GitHub",
      } as Record<string, string>,
      suspendedHint: "The app is suspended on this account, so Glossa cannot act on it. Unsuspend it in the account's settings on GitHub.",
      revokedHint: "The app was uninstalled on GitHub. Its connections no longer work; forget it here to tidy up.",
      repositoriesUnavailable: "GitHub could not be reached for this account, so its repositories are not shown. The connections below are unaffected.",
      forget: "Forget this account",
      forgetTitle: "Forget this GitHub account?",
      forgetBody: (account: string) =>
        `Glossa will forget ${account} and every repository connection under it. The app stays installed on GitHub — uninstall it there if you want it gone for good.`,
      forgetConfirm: "Forget it",
      forgot: "The account and its connections are gone from Glossa.",

      connectionsTitle: "Repository connections",
      connectionsLead: "A repository feeds one project and application. A monorepo feeds several, one per path.",
      noConnections: "No repository is connected yet.",
      repository: "Repository",
      path: "Path",
      pathHint: "A subdirectory of the repository, for a monorepo (apps/web). Leave it empty for the whole repository.",
      wholeRepository: "the whole repository",
      project: "Project",
      application: "Application",
      branch: "Default branch",
      branchHint: "Leave it empty to use the repository's default branch on GitHub.",
      add: "Connect a repository",
      addTitle: "Connect a repository",
      adding: "Connecting…",
      added: (repo: string) => `${repo} is connected.`,
      remove: "Disconnect",
      removeTitle: "Disconnect this repository?",
      removeBody: (repo: string) => `${repo} will stop feeding this project. Nothing changes on GitHub, and nothing already translated is lost.`,
      removeConfirm: "Disconnect it",
      removed: "The repository is disconnected.",
      chooseRepository: "Choose a repository",
      chooseProject: "Choose a project",
      chooseApplication: "Choose an application",
      noRepositories: "This account's app cannot see any repository. Add one to its installation on GitHub.",
      noProjects: "This workspace has no project yet. Create one first.",
      noApplications: "This project has no application yet. Add one in the project's settings.",
      cancel: "Cancel",
      unknownProject: "a project that is gone",
    },
    knowledge: {
      title: "Translation memory & termbase",
      lead: "The workspace's own translation memory and termbase are shared by every project: their matches and terms are offered everywhere. Import TMX and TBX files into them, and export all of them — every project's units and concepts included.",
      back: "Projects",
      importTitle: "Import",
      importLead: "A TMX file adds to the translation memory (it only ever adds: units already there stay unchanged). A TBX file adds and updates concepts of the termbase.",
      noImportRights: "Importing into the workspace's memory and termbase needs the manage permission and the right to write terminology (developers, admins, owners).",
      file: "TMX or TBX file",
      fileHint: "Catalog files (XLIFF, JSON, PO) belong to a project: import them there.",
      notKnowledge: "That isn't a TMX or TBX file. Catalogs are imported in a project.",
      detected: (f: string, kind: string) => `${f}: into the workspace's ${kind.toLowerCase()}.`,
      exportTitle: "Export",
      exportLead: "Everything the workspace holds: its own units or concepts and every project's.",
      what: "What",
      tm: "Translation memory (TMX)",
      termbase: "Termbase (TBX)",
      sourceLocale: "Source locale",
      sourceHint: "Only units with this source locale. Empty: any.",
      targetLocale: "Target locale",
      targetHint: "Add the target locales to keep. None: all.",
      addLocale: "Add",
      removeLocale: (l: string) => `Remove ${l}`,
      chosenTargets: "Target locales",
      start: "Export",
      imports: "Imports",
      exports: "Exports",
      backToWorkspace: "Back to translation memory & termbase",
    },
  },
  auth: {
    signInTitle: "Sign in to Glossa",
    signInLead: "We'll email you a link. No password needed.",
    signInLeadNoEmail: "Sign in with your password or a passkey.",
    noEmailHint: "This server doesn't send email, so there are no sign-in links or password resets by email.",
    email: "Email address",
    sendLink: "Email me a sign-in link",
    sending: "Sending…",
    linkSentTitle: "Check your inbox",
    linkSent: (email: string) => `If ${email} can receive mail, a sign-in link is on its way. It works once, for 15 minutes.`,
    useDifferentEmail: "Use a different address",
    redeeming: "Signing you in…",
    linkInvalid: "This sign-in link is invalid, expired or already used. Request a new one.",
    passkey: "Sign in with a passkey",
    passkeyPrimary: (email: string) => `Continue as ${email} with your passkey`,
    otherMethods: "Other ways to sign in",
    password: "Password",
    totp: "Authentication code",
    totpHint: "Six digits from your authenticator app.",
    signInWithPassword: "Sign in with password",
    forgotPassword: "Forgot your password?",
    noAccount: "New to Glossa?",
    register: "Create an account",
    registerTitle: "Create your Glossa account",
    registerLead: "You'll get a personal workspace; invite a team later.",
    registerLeadNoEmail: "You'll get a personal workspace and be signed in right away. This server doesn't send email, so keep your password safe; you can add a passkey afterwards.",
    displayName: "Your name (optional)",
    newPassword: "Password (at least 12 characters)",
    registerSubmit: "Create account",
    registered: (email: string) => `Almost there: we sent a verification link to ${email}. Open it to sign in.`,
    haveAccount: "Already have an account?",
    resetTitle: "Reset your password",
    resetLead: "We'll email you a link to set a new password.",
    resetUnavailable: "This server doesn't send email, so a password can't be reset by email. Sign in with a passkey if you added one, or ask the server's operator.",
    resetSend: "Email me a reset link",
    resetSent: (email: string) => `If ${email} has an account, a reset link is on its way.`,
    setPasswordTitle: "Choose a new password",
    setPassword: "Set password",
    passwordChanged: "Your password is changed and every session was signed out. Sign in again.",
    backToSignIn: "Back to sign in",
  },
  passkeyPromo: {
    title: "Sign in faster with a passkey",
    body: "Use Touch ID, Windows Hello or your phone instead of waiting for an email.",
    action: "Add a passkey",
    dismiss: "Not now",
  },
  account: {
    title: "Account & security",
    profile: "Profile",
    emailVerified: "verified",
    emailUnverified: "not verified",
    passkeys: "Passkeys",
    passkeysLead: "A passkey signs you in with your device's screen lock. It can't be phished or reused elsewhere.",
    passkeyName: "Passkey name",
    passkeyNameDefault: "This device",
    addPasskey: "Add a passkey",
    passkeyAdded: (name: string) => `Passkey "${name}" added.`,
    passkeysUnsupported: "This browser doesn't support passkeys.",
    passkeysOff: "Passkeys aren't configured on this server, so new ones can't be added. Existing ones stay listed and can be removed.",
    noPasskeys: "No passkeys yet.",
    passkeyColumns: { name: "Name", added: "Added", lastUsed: "Last used", actions: "Actions" },
    neverUsed: "never",
    removePasskey: "Remove",
    removePasskeyLabel: (name: string) => `Remove passkey ${name}`,
    removeTitle: (name: string) => `Remove passkey “${name}”?`,
    removeLead: "It won't sign you in any more, on any device. Sessions it started stay signed in; sign out everywhere to end them.",
    removeConfirm: "Remove passkey",
    passkeyRemoved: (name: string) => `Passkey "${name}" removed.`,
    totp: "Authenticator app (TOTP)",
    totpLead: "A second step for password sign-in.",
    totpOn: "On",
    totpOff: "Off",
    totpStart: "Set up authenticator app",
    totpSecret: "Enter this key in your authenticator app, or open the link on your phone:",
    totpOpen: "Open in authenticator app",
    totpConfirm: "Confirm",
    totpEnabled: "Authenticator app is on. Password sign-in now asks for a code.",
    totpDisable: "Turn off",
    totpDisabled: "Authenticator app is off.",
    sessions: "Sessions",
    sessionsLead: "Sign out on every device, including this one.",
  },
  tenants: {
    createTitle: "New organization",
    createLead: "Organizations share projects with a team. You'll be the owner.",
    name: "Name",
    slug: "Slug",
    slugHint: "Lowercase letters, digits and hyphens; used in URLs.",
    create: "Create organization",
  },
  projects: {
    title: "Projects",
    empty: "No projects yet. A project holds one product's messages, locales and releases.",
    create: "New project",
    createTitle: "New project",
    name: "Name",
    slug: "Slug",
    sourceLocale: "Source locale",
    sourceLocaleHint: "The language messages are written in. It can't change later.",
    defaultSyntax: "Authoring syntax",
    syntaxMf1: "ICU MessageFormat 1",
    syntaxMf2: "MessageFormat 2",
    reviewRequired: "Translations need review before release",
    reviewRequiredHint: "New text waits in “needs review” until a reviewer approves it.",
    submit: "Create project",
    source: (tag: string) => `Source: ${tag}`,
  },
  settings: {
    title: "Project settings",
    general: "General",
    saved: "Settings saved.",
    changedElsewhere: "Someone else changed this project. Reload to see their changes, then apply yours.",
    applications: "Applications",
    applicationsLead: "Runtime surfaces that load this project's messages.",
    platform: "Platform",
    addApplication: "Add application",
    noApplications: "No applications yet.",
    danger: "Delete project",
    dangerLead: "Deletes every message, locale, translation and their history. This can't be undone.",
    deleteConfirm: (slug: string) => `Type ${slug} to confirm`,
    deleteProject: "Delete this project",
  },
  checkPolicy: {
    title: "Pull request check",
    lead:
      "What Glossa reports on a pull request, and what makes it fail. `glossa check` in a terminal " +
      "decides by the same settings, so a green terminal and a red pull request cannot disagree.",
    requireComplete: "Locales that must be complete",
    requireCompleteAll: "Every locale of this project",
    requireCompleteListed: "Only the locales I pick",
    requireCompleteNone: "None of them",
    locales: "Which locales",
    localesHint: "An untranslated key in one of these is reported as the severity below; in any other locale it is only ever a warning.",
    noLocalesPicked: "Pick at least one locale, or choose “None of them”.",
    missingTranslations: "An untranslated key is",
    missingTranslationsError: "an error",
    missingTranslationsWarning: "a warning",
    missingTranslationsHint: "Teams that translate after merging make it a warning: the check still lists what is missing without blocking the pull request.",
    failOn: "Fail the check on",
    failOnError: "Errors",
    failOnWarning: "Errors and warnings",
    failOnNever: "Nothing — only report",
    /** What the settings add up to, in plain language. */
    effectTitle: "What this means",
    effectFailsOnErrors: "A pull request fails when the check finds an error.",
    effectFailsOnWarnings: "A pull request fails when the check finds an error or a warning.",
    effectNeverFails: "A pull request never fails this check. Glossa still reports everything it finds.",
    untranslatedAll: (severity: string) => `A new key with no translation in any locale is ${severity}.`,
    untranslatedListed: (locales: string, severity: string) =>
      `A new key with no translation in ${locales} is ${severity}; in any other locale it is a warning.`,
    untranslatedNone: "A new key with no translation is only ever a warning, in every locale.",
    anError: "an error",
    aWarning: "a warning",
    saved: "Check policy saved.",
  },
  previewOrigins: {
    title: "In-product editing",
    lead:
      "Where this project's in-product editor may run. A page served from one of these origins can ask " +
      "someone to lend it their Glossa permissions for fifteen minutes, so keep the list short and remove " +
      "what you no longer use.",
    columns: { origin: "Origin", label: "Label", added: "Added", actions: "Actions" },
    development: "Development",
    empty: "No origins yet. The editor cannot run anywhere until one is added.",
    origin: "Origin",
    originHint:
      "Scheme, host and port only, e.g. https://preview.example.com. Plain http works on localhost for " +
      "development and nowhere else.",
    label: "Label",
    labelHint: "What your team calls this deployment, e.g. \u201cshared preview\u201d.",
    add: "Allow the editor here",
    needsPermission: "You need permission to manage tokens to change this list.",
    remove: "Remove",
    removeTitle: "Remove this origin?",
    removeConfirm: (origin: string) =>
      `The editor will stop working on ${origin} straight away, and anyone editing there right now will be signed out of it.`,
    registered: (origin: string) => `The editor may now run on ${origin}.`,
    removed: (origin: string) => `The editor no longer runs on ${origin}.`,
    cancel: "Cancel",
  },
  inContext: {
    title: "Allow in-product editing",
    lead: "A page outside Glossa is asking to edit this project's text as you.",
    actingAs: "Acting as",
    you: "you",
    onOrigin: "On",
    willAllow: "It will be able to",
    mayInspect: "see this project's messages and their translations",
    mayEdit: "write translations, in the locales you can write",
    mayAsk: "ask for an AI suggestion and accept or edit it",
    mayTerms: "read your terms and style guides",
    ceiling:
      "Never more than you can do yourself, and never anything else \u2014 it cannot publish a release, " +
      "approve a translation, or change settings, people or keys.",
    shortLived:
      "The editor gets fifteen minutes and has to ask again after that. It only works on the origin above, " +
      "and it is never saved to disk.",
    authorize: "Allow editing",
    authorizing: "Allowing\u2026",
    cancel: "Cancel",
    cancelled: "You chose not to allow in-product editing.",
    granted: "Done \u2014 you can close this window.",
    badRequest: "This link is incomplete. Open the editor from your preview deployment again.",
  },
  device: {
    title: "Sign in a device",
    lead: "Enter the code your terminal shows to sign the Glossa CLI in.",
    code: "Device code",
    codeHint: "Eight letters, shown as XXXX-XXXX. Case and the hyphen don't matter.",
    codeInvalid: "That isn't a device code. Codes are eight consonants, like BCDF-GHJK.",
    lookUp: "Continue",
    lookingUp: "Looking up…",
    found: "Approve this sign-in?",
    device: "Device",
    deviceHint: "The name the device gave itself.",
    requested: "Asked",
    expires: "Code expires",
    when: (relative: string, absolute: string) => `${relative} (${absolute})`,
    warningTitle: "Only approve a sign-in you started yourself",
    warning:
      "Approving signs this device in as you, with all your access. If someone sent you this code, " +
      "or you didn't just run a sign-in command, deny it.",
    approve: "Approve",
    approving: "Approving…",
    deny: "Deny",
    denying: "Denying…",
    otherCode: "Enter a different code",
    approvedTitle: "Device signed in",
    approved: "Device signed in — you can return to your terminal.",
    deniedTitle: "Sign-in denied",
    denied: "Sign-in denied. The device was not signed in; you can close this page.",
  },
  locales: {
    title: "Locales",
    lead: "Every locale this project is translated into. Codes are BCP 47 and stored canonically.",
    add: "Add locale",
    code: "Locale code",
    codeHint: "BCP 47, e.g. de, pt-BR, zh-Hant-TW.",
    preview: (tag: string, name: string, dir: string) => `Adds ${tag} — ${name}, written ${dir === "rtl" ? "right to left" : "left to right"}.`,
    invalid: "That isn't a valid BCP 47 locale code.",
    extension: "Extensions (-u-, -t-) and private use (-x-) aren't allowed.",
    tooLong: "Locale codes can be at most 35 characters.",
    source: "source",
    direction: "Direction",
    ltr: "Left to right",
    rtl: "Right to left",
    name: "Language",
    removeLabel: (tag: string) => `Remove ${tag}`,
    removeConfirm: (tag: string) => `Remove ${tag}? Its translations are kept and come back if you add it again.`,
    fallbackTitle: "Fallback graph",
    fallbackLead:
      "When a translation is missing, runtimes try these locales in order. Rows apply to one locale; “*” applies to every locale without its own row. Without a row, a locale falls back to its parent (de-AT → de), then to the source.",
    from: "When missing in",
    chain: "try, in order",
    everyLocale: "* (every other locale)",
    addRow: "Add rule",
    addToChain: "Add fallback locale",
    moveUp: (tag: string) => `Move ${tag} earlier`,
    moveDown: (tag: string) => `Move ${tag} later`,
    removeFromChain: (tag: string) => `Remove ${tag} from this chain`,
    removeRow: (from: string) => `Remove the rule for ${from}`,
    saveFallback: "Save fallback graph",
    fallbackSaved: "Fallback graph saved.",
    choose: "Choose…",
    issues: {
      fallback_unknown_locale: (l: string) => `${l} isn't a locale of this project.`,
      fallback_self_reference: (l: string) => `${l} can't fall back to itself.`,
      fallback_duplicate: (l: string) => `${l} is listed twice.`,
      fallback_duplicate_row: (l: string) => `There is already a rule for ${l}.`,
      fallback_empty_chain: (l: string) => `Add at least one fallback locale for ${l}.`,
      fallback_cycle: (_l: string, path: string[]) => `This creates a loop: ${path.join(" → ")}.`,
    },
  },
  workspace: {
    title: "Translate",
    targetLocale: "Target locale",
    noTargets: "Add a locale to start translating.",
    addLocales: "Add locales",
    filters: "Filters",
    search: "Search keys and text",
    namespace: "Namespace",
    allNamespaces: "All namespaces",
    coverage: "Show",
    coverageAll: "All messages",
    coverageMissing: (l: string) => `Missing in ${l}`,
    coverageOutdated: (l: string) => `Outdated in ${l}`,
    state: "Message state",
    stateActive: "Active",
    stateObsolete: "Obsolete",
    stateProposed: "Proposed",
    stateProposedHint: "New on an open branch: not in releases until the default branch brings it in",
    stateAll: "All",
    stateProposedFilter: "New on a branch",
    context: {
      legend: "Where it appears",
      show: "Show context filters",
      hide: "Hide context filters",
      route: "Route",
      component: "Component",
      file: "File",
      any: "Any",
      coverage: "Context",
      coverageAll: "Any",
      coverageUnused: "Unused",
      coverageUncaptured: "Not captured",
      loading: "Loading usage data…",
      noData: "No usage data yet: nothing to filter by. Upload usages with glossa extract --upload or the bundler plugin.",
      checking: (done: number, total: number) => `Checking screenshots: ${done.toLocaleString()} of ${total.toLocaleString()}…`,
      capped: (n: number) => `Checked the first ${n.toLocaleString()} messages with a usage.`,
      matched: (n: number) => `${n.toLocaleString()} messages match the context filters.`,
      clear: "Clear context filters",
    },
    messages: "Messages",
    count: (shown: number, loaded: number, done: boolean) =>
      `${shown.toLocaleString()} of ${loaded.toLocaleString()}${done ? "" : "+"} messages`,
    loadingMore: "Loading more…",
    localeStats: (code: string, translated: number, total: number, outdated: number, review: number) =>
      [`${code}: ${translated.toLocaleString()} of ${total.toLocaleString()} translated`, outdated ? `${outdated.toLocaleString()} outdated` : "", review ? `${review.toLocaleString()} need review` : ""]
        .filter(Boolean)
        .join(" · "),
    empty: "No messages match these filters.",
    noMessages: "This project has no messages yet. Push them with the CLI or the API.",
    selectMessage: "Select a message to translate it.",
    status: { missing: "Missing", outdated: "Outdated", translated: "Translated", unknown: "" },
    key: "Key",
    description: "Context",
    noDescription: "No context from the developers yet.",
    maxLength: (n: number) => `Max ${n} characters`,
    source: "Source",
    arguments: "Arguments",
    noArguments: "No arguments",
    markup: "Markup",
    target: "Translation",
    syntax: "Syntax",
    stateLabel: {
      draft: "Draft",
      needs_review: "Needs review",
      approved: "Approved",
      rejected: "Rejected",
    } as Record<string, string>,
    origin: "Origin",
    outdated: "Outdated",
    outdatedLead: (from: number, to: number) => `Translated against source revision ${from}; the source is now at revision ${to}.`,
    showSourceChanges: "Show source changes",
    hideSourceChanges: "Hide source changes",
    sourceChanges: "Changes to the source since this translation",
    notTranslated: "Not translated yet.",
    save: "Save",
    saveApprove: "Save & approve",
    approve: "Approve",
    reject: "Reject",
    requestReview: "Request review",
    saved: "Saved.",
    approved: "Approved.",
    savedForReview: "Saved. Another reviewer has to approve it.",
    rejected: "Rejected.",
    unchanged: "No changes to save.",
    unsaved: "Unsaved changes",
    noWrite: (l: string) => `You can't write ${l} translations in this workspace.`,
    ownText: "You wrote this text, so you can't approve or reject it. Ask another reviewer.",
    selfApproved: "self-approved",
    noReview: (l: string) => `Only reviewers for ${l} can approve or reject.`,
    conflict: "Someone else saved this translation meanwhile. Their version is loaded; your text is kept in the editor.",
    qaTitle: "Structural QA",
    qaFailed: "The translation doesn't match the source's structure. Fix these and save again:",
    qaWarnings: "Warnings",
    invalidText: "The text doesn't parse:",
    preview: "Live preview",
    previewSource: "Source",
    previewTarget: "Translation",
    previewMf1Stale: "The live preview is unavailable right now: it shows the last saved text and updates when you save.",
    previewPaused: "Preview paused: too many previews in a short time. It resumes in a moment.",
    previewInvalid: "This text doesn't parse yet:",
    previewEmpty: "Nothing to preview yet.",
    samples: "Sample values",
    sampleFor: (name: string) => `Sample value for ${name}`,
    pluralForms: (l: string) => `Plural forms in ${l}`,
    formatErrors: "Formatting problems",
    tabs: { editor: "Editor", history: "History" },
    history: "Revisions, newest first",
    historyEmpty: "No revisions yet.",
    revision: (n: number) => `Revision ${n}`,
    kind: { content: "Text", review: "Review" } as Record<string, string>,
    madeAgainst: (n: number) => `against source revision ${n}`,
    by: (who: string) => `by ${who}`,
    canonical: "Canonical MF2",
  },
  releases: {
    title: "Releases",
    lead: "A release is an immutable, signed snapshot of this project's eligible translations. Environments point at releases; promoting and rolling back move the pointer without rebuilding.",
    readOnly: "You can see releases here. Publishing, promoting and rolling back need the developer, admin or owner role.",
    publish: "Publish release",
    environments: "Environments",
    releases: "Releases",
    noReleases: "Nothing published yet. Publish a release to make this project's text available to runtimes.",
    loadMore: "Load more releases",
    version: (v: number) => `v${v}`,
    nothingServed: "Nothing published here yet",
    serving: (v: string) => `Serving ${v}`,
    lastChange: { publish: "Published", promote: "Promoted", rollback: "Rolled back" } as Record<string, string>,
    byWhen: (what: string, who: string) => `${what} by ${who}`,
    ships: "Ships",
    state: { draft: "Draft", needs_review: "Needs review", approved: "Approved" } as Record<string, string>,
    outdatedIncluded: "outdated included",
    outdatedExcluded: "outdated excluded",
    policyText: (states: string, outdated: string) => `${states}; ${outdated}`,
    // Visible label + a visually hidden suffix, so the accessible name starts with what's shown.
    promote: "Promote",
    toEnv: (env: string) => ` to ${env}`,
    rollback: "Roll back",
    history: "History",
    editPolicy: "Policy",
    ofEnv: (env: string) => ` of ${env}`,
    ofRelease: (v: string) => ` ${v}`,
    columns: {
      release: "Release",
      builtFor: "Built for",
      serving: "Serving",
      locales: "Locales",
      messages: "Messages",
      note: "Note",
      published: "Published",
      actions: "Actions",
    },
    // publish
    publishTitle: "Publish a release",
    environment: "Environment",
    note: "Note (optional)",
    noteHint: "What this release is for, e.g. “Checkout copy for the spring sale”.",
    willShip: "What will ship",
    policyLead: (env: string, policy: string) => `${env} ships translations that are: ${policy}.`,
    firstRelease: (env: string) => `Nothing is published to ${env} yet: every eligible translation ships.`,
    dryRunChecking: "Checking what would ship…",
    dryRunSummary: (messages: number, locales: number, fresh: number, bytes: string) =>
      `${messages.toLocaleString()} ${messages === 1 ? "message" : "messages"} in ${locales} ${locales === 1 ? "locale" : "locales"} · ${fresh} new ${fresh === 1 ? "artifact" : "artifacts"} to upload · ${bytes}`,
    dryRunAgainst: (env: string, v: string) => `Compared with ${v}, which ${env} serves now:`,
    dryRunUnchanged: (env: string) => `Nothing would change: ${env} already ships this text.`,
    dryRunNote: "A dry run: nothing is stored until you publish. A translation saved meanwhile ships too.",
    dryRunFailed: "Glossa couldn't check what would ship. You can still publish; the changes are shown once it's built.",
    notReleasable: (n: number) => `This can't be published yet: ${n} ${n === 1 ? "problem" : "problems"} to fix first.`,
    publishTo: (env: string) => `Publish to ${env}`,
    publishing: "Publishing…",
    published: (v: string, env: string) => `Published ${v} to ${env}.`,
    changesSince: (v: string) => `Changes since ${v}, per locale`,
    everythingNew: "Everything is new in this environment.",
    noChanges: "Nothing changed: it ships the same text as before.",
    done: "Done",
    // promote
    promoteTitle: "Promote a release",
    release: "Release",
    target: "To environment",
    choose: "Choose…",
    pointerMove: (env: string, from: string, to: string) => `${env}: ${from} → ${to}`,
    noneYet: "nothing",
    alreadyServing: (env: string, v: string) => `${env} already serves ${v}. Nothing would change.`,
    ineligible: (env: string, v: string, states: string) =>
      `${v} can't go to ${env}: it was built with ${states} translations, which ${env} doesn't ship. ` +
      (env === "production" ? "Publish to staging (approved text, like production) and promote that release, or publish to production directly." : `Publish to ${env} instead, or change its policy.`),
    ineligibleOutdated: (env: string, v: string) => `${v} can't go to ${env}: it ships outdated translations, which ${env} excludes.`,
    promoteConfirm: (v: string, env: string) => `Promote ${v} to ${env}`,
    promoted: (v: string, env: string) => `${env} now serves ${v}.`,
    pointerOnly: "Only the pointer moves: nothing is rebuilt, and runtimes pick it up within a minute.",
    // rollback
    rollbackTitle: (env: string) => `Roll back ${env}`,
    rollbackTo: "Back to",
    noRollback: (env: string) => `${env} hasn't served an earlier release, so there's nothing to roll back to.`,
    rollbackConfirm: (env: string, v: string) => `Roll ${env} back to ${v}`,
    rolledBack: (env: string, v: string) => `${env} is back on ${v}.`,
    // policy
    policyTitle: (env: string) => `What ships to ${env}`,
    policyStates: "Review states that ship",
    policyStatesHint: "Rejected translations never ship. Promotions into this environment must fit this policy too.",
    includeOutdated: "Ship outdated translations",
    includeOutdatedHint: "Translations made against an older version of the source text.",
    policyNeedsState: "Choose at least one review state.",
    policySaved: (env: string) => `${env}'s policy is saved. It applies from the next publish; the current release keeps serving.`,
    savePolicy: "Save policy",
    // history
    historyTitle: (env: string) => `Deployments to ${env}`,
    historyEmpty: "No deployments yet.",
    historyColumns: { number: "#", action: "Change", release: "Release", by: "By", when: "When" },
    from: (v: string) => `from ${v}`,
    // detail
    detailTitle: (v: string) => `Release ${v}`,
    backToReleases: "All releases",
    builtFor: "Built for",
    policy: "Policy",
    publishedBy: "Published",
    parent: "Previous release here",
    noParent: "None: the first release of its environment",
    digest: "Manifest digest",
    artifacts: "Artifacts",
    artifactsText: (n: number, fresh: number, bytes: string) => `${n} (${fresh} new), ${bytes}`,
    perLocale: "Per locale",
    diffTo: (v: string) => `Compared with ${v}`,
    diffToNothing: "Compared with nothing: the first release of its environment",
    localeColumns: { locale: "Locale", messages: "Messages", outdated: "Outdated", added: "Added", changed: "Changed", removed: "Removed" },
    showIds: (n: number) => `Show ${n} message ${n === 1 ? "ID" : "IDs"}`,
    sourceLocale: "source",
    notInRelease: "not in this release",
  },
  deliveryKeys: {
    title: "Delivery keys",
    lead: "Runtimes load releases from glossa-edge with a publishable delivery key. It is public by design (it ships in browser bundles), read-only, scoped to this project, and grants nothing on the API.",
    name: "Name",
    nameHint: "What uses it, e.g. “web” or “go-emails”.",
    create: "Create delivery key",
    creating: "Creating…",
    empty: "No delivery keys yet.",
    columns: { name: "Name", key: "Key", reads: "Reads", created: "Created", status: "Status", actions: "Actions" },
    scope: "What it reads",
    scopeHint:
      "Anything outside this answers 404 at the edge. Branch previews hold text that hasn't merged, so a key shipped in a production bundle shouldn't read them.",
    previewLabel: "Branch previews (pull request environments)",
    scopeEmpty: "Choose at least one environment, or branch previews.",
    branchPreviews: "branch previews",
    changeScope: "Change scope",
    changeScopeLabel: (name: string) => `Change what ${name} reads`,
    changeScopeTitle: (name: string) => `What “${name}” reads`,
    changeScopeLead:
      "The key itself doesn't change, so bundles that ship it keep working. glossa-edge follows within about 30 seconds, plus any CDN caching.",
    changeScopeConfirm: "Save",
    scopeChanged: (name: string, reads: string) => `${name} now reads ${reads}.`,
    active: "Active",
    revoked: "Revoked",
    revoke: "Revoke",
    revokeLabel: (name: string) => `Revoke ${name}`,
    revokeTitle: (name: string) => `Revoke “${name}”?`,
    revokeBody: (key: string) =>
      `Runtimes using ${key} stop receiving new releases from the edge within about 30 seconds, plus any CDN caching; they keep the release they already loaded. A revoked key can't be restored.`,
    revokeConfirm: "Revoke key",
    revokedDone: (name: string) => `${name} is revoked.`,
    createdTitle: (name: string) => `Delivery key “${name}”`,
    createdLead: "Copy the key now: later Studio shows only its beginning. Paste one of these snippets into your app to load this project's releases.",
    key: "Delivery key",
    copy: "Copy",
    copyLabel: (what: string) => `Copy ${what}`,
    copied: "Copied.",
    copyFailed: "Couldn't copy: select the text and copy it yourself.",
    snippetEnv: "Environment",
    snippets: "Integration snippets",
    snippetOf: (runtime: string) => `${runtime} snippet`,
    install: "Install",
    edgePlaceholder: (edge: string) => `Replace ${edge} with your glossa-edge origin. Set GLOSSA_STUDIO_EDGE_URL on the Studio container to fill it in.`,
    signed: "The snippets pin this project's signing keys, so a manifest without a valid signature is rejected.",
  },
  assist: {
    label: "What Glossa knows about this message",
  },
  tm: {
    title: "Translation memory",
    none: "No similar approved translations yet.",
    kind: { context: "Same key, exact", exact: "Exact match", fuzzy: "Fuzzy match" } as Record<string, string>,
    insert: "Insert",
    sourceDiff: "Its source, compared with this one:",
    from: (key: string) => `From ${key}`,
    scopeProject: "this project",
    scopeTenant: "workspace-wide",
    variablesKept: "a variable kept its name — check it",
    fallbackMf2: "In MessageFormat 2: MF1 can't express this one, so inserting it switches the translation to MF2.",
  },
  concordance: {
    title: "Concordance",
    phrase: "Phrase to look up",
    placeholder: "How was … translated?",
    side: "Search in",
    inSource: (l: string) => `in source (${l})`,
    inTarget: (l: string) => `in translation (${l})`,
    search: "Search",
    results: "Earlier translations containing the phrase",
    none: "Nothing in the translation memory contains that phrase.",
    similarity: (x: number) => `similarity ${x.toFixed(2)}`,
  },
  terms: {
    title: "Terminology",
    none: "No termbase terms in this source.",
    status: { preferred: "Preferred", admitted: "Admitted", deprecated: "Deprecated", forbidden: "Forbidden" } as Record<string, string>,
    avoid: (status: string) => `${status} — avoid`,
    targetTerms: (l: string) => `Terms in ${l}`,
    noTarget: (l: string) => `No ${l} term for this concept yet.`,
    findingsTitle: "Terminology",
    severity: { error: "error", warning: "warning" },
    forbiddenUsed: (t: string) => `“${t}” is a term to avoid here.`,
    missingTerm: (t: string) => `“${t}” is in the source, but the translation uses none of its approved terms.`,
    useInstead: (s: string[]) => `Use: ${s.join(", ")}`,
    checkStale: "The terminology check is paused until the text parses.",
    checkClean: "The translation uses the termbase's terms.",
  },
  where: {
    title: "Where it appears",
    inCode: "In the code",
    onScreen: "On screen",
    noRoute: "No route",
    noComponent: "No component",
    kind: {
      t: "t()",
      component: "<GlossaText>",
      element: "<glossa-text>",
      accessor: "typed accessor",
      template: "template",
    } as Record<string, string>,
    fromBranch: (branch: string) => `on ${branch}`,
    noLink: "Connect the repository to open these lines in Git.",
    moreUsages: (n: number) => `Showing the first ${n.toLocaleString()} usages.`,
    noData: "No usage data yet.",
    noDataHint:
      "Usages come from the build: run glossa extract --upload, add @klarlabs-studio/glossa/unplugin to the bundler and push its .glossa/usages.json, and run glossa capture --upload for screenshots.",
    unused: "Unused",
    unusedLead: "No current build uses this message.",
    unusedHint: "Dynamic keys are invisible to every collector, so this is only reported — nothing is obsoleted automatically.",
    notCaptured: "Not captured",
    notCapturedLead: "No screenshot shows this message yet.",
    notCapturedHint: "Add a route that shows it to the capture plan (capture: in glossa.yaml) and run glossa capture --upload.",
    noUsages: "No file uses this message in the current builds.",
    screenshotLocale: "Screenshot locale",
    localeFallback: (wanted: string, shown: string) => `No ${wanted} screenshot of this screen yet; showing ${shown}.`,
    notVisible: "On this screen, but not visible: it rendered zero-size or off-screen.",
    screen: (route: string, width: number, height: number, locale: string) => `${route} · ${width}×${height} · ${locale}`,
    alt: (route: string, locale: string) => `Screenshot of ${route} in ${locale}, the message outlined and its surroundings dimmed`,
    openFull: "Full screenshot",
    lightbox: (route: string, locale: string) => `${route} — full screenshot (${locale})`,
    moreScreens: (n: number) => (n === 1 ? "Show 1 more screen" : `Show ${n} more screens`),
    truncatedCaptures: (n: number) => `Showing the first ${n.toLocaleString()} screenshots.`,
    thisMessage: "This message",
  },
  style: {
    paneTitle: (locale: string, ns: string) => `Style for ${locale} · ${ns}`,
    none: "No style guide applies here yet.",
    rules: "Style rules",
    good: "Do",
    bad: "Don't",
    sources: (n: number) => (n === 1 ? "From 1 guide." : `Merged from ${n} guides, the narrowest winning.`),
    summary: {
      address: "Address",
      tone: "Tone",
      quotes: "Quotes",
      nested: (q: string) => `(nested ${q})`,
      dash: "Dash",
      dashes: { hyphen: "hyphen (-)", en: "en dash (–)", em: "em dash (—)" },
      ellipsis: "Ellipsis",
      spaceBeforeUnit: "Space before units",
      spaceBeforePunctuation: "Space before punctuation",
      serialComma: "Serial comma",
      yes: "yes",
      no: "no",
      numbers: "Numbers",
      decimal: (s: string) => `decimal “${s}”`,
      grouping: (s: string) => `grouping “${s}”`,
      numberNotes: "Number notes",
      dates: "Dates",
      dateNotes: "Date notes",
    },
    title: "Style guides",
    lead: "Style is structured, not prose, so the AI and QA can follow it. Guides stack from the workspace to this project, a locale and a namespace; the narrowest wins field by field.",
    readOnly: "You can read the style guides. Changing them needs the developer, admin or owner role.",
    newGuide: "New style guide",
    empty: "No style guides yet.",
    scope: "Applies to",
    scopeLevel: { tenant: "The whole workspace", project: "This project", locale: "This project, one locale", namespace: "This project, one locale and namespace" } as Record<string, string>,
    tenantScope: "Workspace",
    locale: "Locale",
    namespace: "Namespace",
    name: "Name",
    version: (n: number) => `v${n}`,
    updated: (who: string, when: string) => `Updated by ${who}, ${when}`,
    edit: (name: string) => `Edit ${name}`,
    history: (name: string) => `History of ${name}`,
    remove: (name: string) => `Delete ${name}`,
    dialogNew: "New style guide",
    dialogEdit: (name: string) => `Edit “${name}”`,
    fields: "Fields",
    inherit: "Inherit",
    register: "Formality",
    registers: { formal: "Formal", informal: "Informal", neutral: "Neutral" } as Record<string, string>,
    pronoun: "Pronoun",
    pronounHint: "How the reader is addressed, e.g. du, Sie, vous.",
    tone: "Tone",
    toneHint: "Tags, separated by commas: concise, friendly, …",
    quotes: "Quotes",
    nestedQuotes: "Nested quotes",
    dash: "Dash",
    ellipsis: "Ellipsis",
    spaceBeforeUnit: "Space before units",
    spaceBeforePunctuation: "Space before punctuation",
    serialComma: "Serial comma",
    yes: "Yes",
    no: "No",
    decimal: "Decimal separator",
    grouping: "Grouping separator",
    numberNotes: "Number notes",
    dateFormat: "Date pattern (CLDR)",
    dateNotes: "Date notes",
    rulesTitle: "Rules",
    addRule: "Add rule",
    ruleTitle: "Rule",
    ruleId: "ID",
    ruleIdHint: "Lowercase letters, digits, - and _. A narrower guide's rule with the same ID replaces this one.",
    rationale: "Why",
    goodExamples: "Good examples (one per line)",
    badExamples: "Bad examples (one per line)",
    disableRule: "Switch off the broader rule with this ID",
    removeRule: (n: number) => `Remove rule ${n}`,
    invalidRuleId: (id: string) => `“${id}” isn't a valid rule ID.`,
    saveGuide: "Save guide",
    created: "Style guide created.",
    saved: "Style guide saved.",
    deleted: "Style guide deleted.",
    confirmDelete: (name: string) => `Delete “${name}”?`,
    confirmDeleteLead: "Its versions stay readable; broader guides apply again where it did.",
    versions: (name: string) => `Versions of “${name}”`,
    versionAction: { created: "Created", updated: "Updated", deleted: "Deleted" } as Record<string, string>,
    preview: "What applies",
    previewLead: "The effective style for a locale and namespace of this project.",
    previewNamespace: "Namespace",
    changedElsewhere: "Someone else changed this guide meanwhile. Reload it and try again.",
  },
  termbase: {
    title: "Termbase",
    lead: "Concepts and their terms per locale: the words to use, and the ones to avoid. Terms are highlighted in the editor and checked as translators type.",
    readOnly: "You can read the termbase. Changing it needs the developer, admin or owner role.",
    search: "Search terms and definitions",
    locale: "Locale",
    allLocales: "All locales",
    newConcept: "New concept",
    empty: "No concepts yet.",
    noMatch: "No concepts match.",
    scopeProject: "This project",
    scopeTenant: "Workspace",
    scope: "Scope",
    scopeHint: "A workspace concept applies to every project; the scope can't change later.",
    definition: "Definition",
    domain: "Domain",
    note: "Note",
    terms: "Terms",
    term: "Term",
    status: "Status",
    partOfSpeech: "Part of speech",
    pos: { "": "—", noun: "Noun", verb: "Verb", adjective: "Adjective", adverb: "Adverb", proper_noun: "Proper noun", phrase: "Phrase", other: "Other" } as Record<string, string>,
    caseSensitive: "Case-sensitive",
    termNote: "Usage note",
    addTerm: "Add term",
    removeTerm: (n: number) => `Remove term ${n}`,
    dialogNew: "New concept",
    dialogEdit: "Edit concept",
    saveConcept: "Save concept",
    created: "Concept added.",
    saved: "Concept saved.",
    deleted: "Concept deleted.",
    edit: (label: string) => `Edit ${label}`,
    history: (label: string) => `History of ${label}`,
    remove: (label: string) => `Delete ${label}`,
    confirmDelete: (label: string) => `Delete “${label}”?`,
    confirmDeleteLead: "Its history stays readable. Its terms stop being highlighted and checked.",
    revisions: (label: string) => `History of “${label}”`,
    revisionAction: { created: "Created", updated: "Updated", deleted: "Deleted" } as Record<string, string>,
    version: (n: number) => `v${n}`,
    needsTerm: "Add at least one term.",
    changedElsewhere: "Someone else changed this concept meanwhile. Reload it and try again.",
  },
  ai: {
    title: "AI suggestion",
    none: "No AI suggestion for this message yet. “Fill with AI” asks for one.",
    outdated: "The source has changed since this suggestion was made.",
    status: { pending: "Pending", accepted: "Accepted", rejected: "Rejected", auto_applied: "Auto-applied", superseded: "Superseded" } as Record<string, string>,
    band: { very_high: "Very high confidence", high: "High confidence", medium: "Medium confidence", low: "Low confidence" } as Record<string, string>,
    score: (x: string) => `score ${x}`,
    notCertainty: "Confidence orders review; it isn't a promise that the text is right.",
    action: { auto_approve: "Auto-approve", approve_recommended: "Approval recommended", review_required: "Review required" } as Record<string, string>,
    risks: "Risks",
    riskTag: (t: string) => ({ legal: "Legal text", marketing: "Marketing text", term_forbidden: "Forbidden term", max_length: "Too long", missing_plural_categories: "Missing plural forms", markup: "Markup" })[t] ?? t.replaceAll("_", " "),
    findings: "Findings",
    why: "Why?",
    factorsCaption: "What moved the confidence score",
    factor: "Factor",
    effect: "Effect",
    reason: "Why",
    factorName: (f: string) =>
      ({
        origin: "Origin",
        tm_match: "Translation-memory match",
        term_forbidden: "Forbidden term",
        term_missing: "Missing term",
        repairs: "Repairs",
        qa_warnings: "QA warnings",
        self_assessment: "Model's self-assessment",
        formality: "Formality",
        max_length: "Max length",
        length_ratio: "Length ratio",
        risk_tag: "Risk tag",
        markup_density: "Markup density",
        missing_plural_categories: "Missing plural forms",
      })[f] ?? f.replaceAll("_", " "),
    provenance: "Provenance",
    origin: "Made by",
    originName: { ai: "AI model", translation_memory: "Translation memory (no model call)" } as Record<string, string>,
    model: "Provider / model",
    promptVersion: "Prompt version",
    tmUnits: "TM units used",
    terms: "Terms used",
    styleVersion: "Style guide version",
    repairs: "Structural repairs",
    cost: "Cost",
    tokens: (n: number) => `${n.toLocaleString()} tokens`,
    count: (n: number) => n.toLocaleString(),
    noneUsed: "none",
    accept: "Accept",
    edit: "Edit…",
    rejectEllipsis: "Reject…",
    reject: "Reject",
    acceptEdit: "Accept edit",
    editLabel: "Your edit (MessageFormat 2)",
    editHint: (chord: string) => `${chord} accepts, Esc cancels. Your changes are recorded to improve future suggestions.`,
    reasonLabel: "Reason (optional)",
    acceptedNotice: "Accepted: it's now the translation.",
    acceptedEditNotice: "Your edit is accepted as the translation.",
    rejectedNotice: "Rejected. Nothing was written.",
    editedBy: (d: number) => `Accepted after an edit of ${d} character${d === 1 ? "" : "s"}.`,
    noDecide: (l: string) => `Accepting and rejecting ${l} suggestions needs permission to translate ${l}.`,
  },
  fill: {
    button: "Fill with AI…",
    queueLink: "Review queue",
    title: (l: string) => `Fill ${l} with AI`,
    scope: (l: string, ns: string | undefined, keys: number | undefined) =>
      keys !== undefined
        ? `Of the ${keys.toLocaleString()} message${keys === 1 ? "" : "s"} your search shows${ns ? ` in ${ns}` : ""}, those selected below get an AI suggestion in ${l}.`
        : `Every message${ns ? ` in ${ns}` : ""} selected below gets an AI suggestion in ${l}.`,
    select: (l: string) => `Translate messages whose ${l} translation is`,
    selects: {
      missing: "Missing (none yet, or rejected)",
      outdated: "Outdated (made against an older source)",
      missing_or_outdated: "Missing or outdated",
    } as Record<"missing" | "outdated" | "missing_or_outdated", string>,
    lead: "Suggestions use the translation memory, termbase and style guides, and wait in the review queue unless the project's policy auto-approves them. Messages in sensitive namespaces are never sent to a provider.",
    previewTitle: "What this fill would do",
    previewing: "Checking what a fill would do…",
    planMessages: (n: number) => `${n.toLocaleString()} message${n === 1 ? "" : "s"} to fill:`,
    planProvider: (n: number) => `${n.toLocaleString()} call${n === 1 ? "s" : ""} an AI provider`,
    planTmExact: (n: number) => `${n.toLocaleString()} reuse${n === 1 ? "s" : ""} an exact translation-memory match — no provider call`,
    planExisting: (n: number) => `${n.toLocaleString()} already ${n === 1 ? "has a job" : "have jobs"}, reused rather than run again`,
    refused: (why: string, n: number) =>
      `${n.toLocaleString()} won't reach a provider: ${({ sensitive: "in a sensitive namespace (never sent)", provider_consent: "sending text to AI providers is off", no_route: "no route to an enabled provider for this task", budget_exceeded: "over this month's AI budget" } as Record<string, string>)[why] ?? why}`,
    showKeys: (l: string, n: number) => `Show the ${n.toLocaleString()} key${n === 1 ? "" : "s"} (${l})`,
    cost: (estimated: string, max: string) => `Estimated cost ${estimated}; at most ${max}, which the budget reserves while jobs run.`,
    unpriced: "A routed model has no price, so its calls count as $0 here. An admin can set prices in AI settings.",
    nothingPlanned: "Nothing to fill: no message in scope has a translation in the selected state.",
    start: (n: number) => (n ? `Fill ${n.toLocaleString()} message${n === 1 ? "" : "s"}` : "Fill with AI"),
    queued: (created: number, existing: number) =>
      `${created.toLocaleString()} job${created === 1 ? "" : "s"} queued${existing ? `, ${existing.toLocaleString()} already there` : ""}.`,
    skipped: (entries: Array<[string, number]>) =>
      `Skipped: ${entries.map(([why, n]) => `${n} ${({ sensitive: "in sensitive namespaces", up_to_date: "up to date", not_selected: "not in the selected state", limit: "over the limit" } as Record<string, string>)[why] ?? why}`).join(", ")}.`,
    progressLabel: "Progress",
    progress: (p: { total: number; active: number; succeeded: number; skipped: number; failed: number }) =>
      p.active
        ? `${(p.total - p.active).toLocaleString()} of ${p.total.toLocaleString()} done…`
        : `Done: ${p.succeeded} suggested, ${p.skipped} skipped, ${p.failed} failed.`,
    nothing: "Nothing to fill: every message in scope has a translation.",
    failure: (code: string, n: number) =>
      `${n} × ${({ provider_consent: "consent to use AI providers is off", sensitive: "sensitive namespace", invalid_output: "the model's answer wasn't valid", budget_exceeded: "over the monthly budget", no_route: "no provider for this task", provider_error: "the provider failed", invalid_source: "the source doesn't parse", dead: "gave up after retries", cancelled: "cancelled" } as Record<string, string>)[code] ?? code}`,
    warning: (w: string) =>
      ({
        provider_consent_off: "Sending text to AI providers is off for this workspace, so only exact translation-memory matches are reused. An admin can turn it on in AI settings.",
        no_budget: "The monthly AI budget is 0, so no provider is called. An admin can set a budget in AI settings.",
        no_provider: "No AI provider is configured for this workspace. An admin can add one in AI settings.",
      })[w] ?? w,
    cancelQueued: "Cancel queued jobs",
    openQueue: "Open the review queue",
    keepRunning: "Close (keeps running)",
  },
  review: {
    title: "Review queue",
    lead: "AI suggestions waiting for a person, riskiest first: the lowest confidence, then the most risk tags. The routing policy decides what lands here.",
    locale: "Locale",
    allLocales: "All your locales",
    list: "Suggestions to review",
    empty: "Nothing to review. New suggestions appear here as AI jobs finish.",
    loadMore: "Load more",
    count: (n: number, more: boolean) => `${n.toLocaleString()}${more ? "+" : ""} waiting`,
    source: "Source",
    renamed: (key: string) => `Now ${key}.`,
    sourceChanged: (was: number, now: number) =>
      `This suggestion translates source revision ${was}; the source is now at revision ${now}, so it can't be accepted. Open it in the editor to translate the current source.`,
    sourceObsolete: "The message is obsolete: it's no longer in the code.",
    sourceGone: "The message no longer exists.",
    openInEditor: "Open in the editor",
    accept: "Accept",
    edit: "Edit",
    reject: "Reject",
    acceptEdit: "Accept edit",
    editLabel: "Your edit (MessageFormat 2)",
    batch: (n: number) => `Accept ${n} recommended`,
    batchTitle: "Accept every recommended suggestion?",
    batchLead: (n: number) =>
      `${n} suggestion${n === 1 ? " is" : "s are"} routed “approval recommended” by the project's policy. Each becomes the translation. Suggestions that need review are left for you.`,
    batchConfirm: (n: number) => `Accept ${n}`,
    batchDone: (ok: number, failed: number) => `Accepted ${ok}${failed ? `; ${failed} couldn't be accepted` : ""}.`,
    accepted: (key: string, l: string) => `Accepted ${key} (${l}).`,
    acceptedEdit: (key: string, l: string) => `Accepted your edit of ${key} (${l}).`,
    rejected: (key: string, l: string) => `Rejected ${key} (${l}).`,
    noDecide: (l: string) => `You can't decide ${l} suggestions.`,
    keys: "a accept · e edit · r reject · j/k move",
  },
  aiSettings: {
    title: "AI",
    lead: "Which models translate for this workspace, what they may see and spend, and how their suggestions are routed. Settings above the line apply to the whole workspace; below it, to this project.",
    readOnly: "You can see the AI settings. Changing them needs the admin or owner role.",
    workspace: "Workspace",
    project: "This project",
    consentTitle: "Data handling",
    consentLead: "Messages can hold unreleased product copy, customer templates or legal text. Nothing is sent to an AI provider unless you allow it here.",
    consentPoints: [
      "When on, a provider receives exactly what the translation agent looked up for one message: its source and context, matching terms, style rules and translation-memory matches. Every call is logged below (“Disclosures”).",
      "Namespaces tagged sensitive are never sent, whatever this says.",
      "When off, exact translation-memory matches are still reused; nothing else is translated by AI.",
    ],
    consentOn: "Sending text to AI providers is allowed.",
    consentOff: "Sending text to AI providers is off.",
    consentChanged: (who: string, when: string) => `Changed by ${who}, ${when}.`,
    allow: "Allow sending text…",
    disallow: "Stop sending text",
    confirmAllow: "Allow sending text to AI providers?",
    confirmAllowLead: "From now on, AI jobs in every project of this workspace send message text to the configured providers under their terms. You can turn this off at any time; what was already sent stays logged.",
    confirmAllowButton: "Allow",
    providers: "Providers",
    providersLead: "Bring your own keys. A key is sealed (encrypted) when stored and never shown again, not even to admins.",
    noProviders: "No providers yet. Routing uses a provider named “anthropic” by default.",
    name: "Name",
    nameHint: "What routing refers to, e.g. anthropic or mistral-eu.",
    kind: "API",
    kinds: { anthropic: "Anthropic", openai_compatible: "OpenAI-compatible", gemini: "Gemini" } as Record<string, string>,
    baseUrl: "Endpoint (base URL)",
    baseUrlHint: "https only. Required for OpenAI-compatible servers; empty uses the API's default.",
    models: "Allowed models",
    modelsHint: "Comma-separated; empty allows any model.",
    anyModel: "any model",
    enabled: "Enabled",
    disabled: "Disabled",
    apiKey: "API key",
    apiKeyHint: "Stored sealed, never shown again. Leave empty to keep the current key.",
    apiKeyNewHint: "Stored sealed, never shown again — keep your own copy if you need it.",
    keySet: "Key stored (sealed)",
    keyMissing: "No key",
    clearKey: "Remove the stored key",
    addProvider: "Add provider",
    editProvider: (n: string) => `Edit ${n}`,
    removeProvider: (n: string) => `Remove ${n}`,
    confirmRemove: (n: string) => `Remove provider “${n}”?`,
    confirmRemoveLead: "Its key is deleted. A routing policy that uses it has to change first.",
    providerSaved: "Provider saved.",
    budgetTitle: "Budget and spend",
    budget: "Monthly budget (USD)",
    budgetHint: "Provider spend per calendar month (UTC). 0 stops all provider calls.",
    invalidBudget: "Enter an amount in dollars, like 25 or 12.50.",
    concurrency: "Jobs running at once",
    saveBudget: "Save budget",
    budgetSaved: "Budget saved.",
    spent: (spent: string, budget: string) => `${spent} of ${budget} spent this month`,
    remaining: (r: string) => `${r} left`,
    calls: (n: number) => `${n.toLocaleString()} call${n === 1 ? "" : "s"}`,
    spendChart: "Spend per day this month",
    spendChartEmpty: "No provider calls this month.",
    spendTable: "Show as a table",
    byModel: "By provider and model",
    provider: "Provider",
    model: "Model",
    cost: "Cost",
    tokens: "Tokens (in / out)",
    prices: "Prices",
    pricesLead: "USD per million tokens. Your overrides replace the deployment's defaults; an unpriced model costs 0 and is flagged.",
    priceKey: "Provider/model",
    priceIn: "Input",
    priceOut: "Output",
    override: "Override",
    removeOverride: (k: string) => `Remove override for ${k}`,
    addOverride: "Add override",
    pricesSaved: "Prices saved.",
    routingTitle: "Routing policy",
    routingLead: "Which provider and model each task uses, per target locale, with fallbacks in order.",
    routingSource: { default: "The default policy applies (Anthropic).", tenant: "The workspace's policy applies.", project: "This project has its own policy." } as Record<string, string>,
    routingScope: "Edit the policy of",
    routingScopes: { project: "this project", tenant: "the workspace" } as Record<string, string>,
    task: "Task",
    tasks: { translate: "Translate", review: "Review", explain: "Explain", assess: "Self-assessment" } as Record<string, string>,
    ruleLocales: "Locales",
    ruleLocalesHint: "Comma-separated; empty applies to all.",
    routes: "Routes (first preferred)",
    maxTokens: "Max tokens",
    temperature: "Temperature",
    effort: "Effort",
    effortDefault: "Default",
    addRoute: "Add fallback",
    removeRoute: (n: number) => `Remove route ${n}`,
    addRule: "Add rule",
    removeRule: (n: number) => `Remove rule ${n}`,
    saveRouting: "Save policy",
    routingSaved: "Routing policy saved.",
    resetProject: "Use the workspace's policy",
    projectPolicy: "Project policy",
    namespaceTags: "Namespace tags",
    namespaceTagsLead: "Sensitive: never sent to a provider, always translated by people. Legal and marketing: riskier, reviewed first.",
    namespace: "Namespace",
    tagNames: { sensitive: "Sensitive", legal: "Legal", marketing: "Marketing" } as Record<string, string>,
    addNamespace: "Add namespace",
    removeNamespace: (ns: string) => `Remove ${ns}`,
    noTags: "No namespace is tagged.",
    autoTranslate: "Auto-translate",
    autoTranslateLead: "New and outdated messages in these locales get an AI suggestion automatically.",
    reviewRouting: "Review routing",
    reviewRoutingLead: "Suggestions scoring at or above the first line are recommended for approval; below it, they need review.",
    recommendMin: "Approval recommended from score",
    autoApprove: "Auto-approve high-confidence suggestions",
    autoApproveWarning: "Auto-approved text becomes an approved translation without anyone reading it, and ships to the environments below. Keep this off unless your evals and edit rates say it's safe.",
    autoApproveMin: "Auto-approve from score",
    autoApproveEnvironments: "Environments auto-approved text ships to",
    autoApproveEnvironmentsHint: "Comma-separated; each must ship approved translations only.",
    forceReview: "Always review when these factors apply",
    forceReviewHint: "Comma-separated factor names, e.g. term_forbidden, max_length.",
    saveProject: "Save project policy",
    projectSaved: "Project policy saved.",
    insights: "Insights",
    metrics: "Acceptance per locale (last 30 days)",
    metricsEmpty: "No decisions on suggestions yet.",
    locale: "Locale",
    acceptance: "Accepted",
    acceptanceRate: "Acceptance rate",
    editDistance: "Mean edit distance",
    rejected: "Rejected",
    edited: "Edited",
    disclosures: "Disclosures",
    disclosuresLead: "Every provider call, with exactly what the provider received, newest first.",
    disclosuresEmpty: "No provider has seen any text from this project.",
    when: "When",
    message: "Message",
    whatWasSent: "What was sent",
    baseline: "Eval baseline",
    baselineLead: "The golden-set metrics committed with this server. A prompt or model change may not regress them.",
    pair: "Pair",
    cases: "Cases",
    structural: "Structural",
    terminology: "Terminology",
    formality: "Formality",
    editRatio: "Edit ratio",
    changedElsewhere: "Someone else changed these settings meanwhile. They're reloaded; make your change again.",
    notLoaded: "These settings haven't loaded yet. Reload the page and try again.",
    percent: (x: number) => `${Math.round(x * 100)}%`,
  },
  integration: {
    title: "Import & export",
    lead: "Bring translations, translation memory and terminology in from files, and take them out again. An import checks every entry first; merging never replaces an approved translation.",
    importFile: "Import a file",
    export: "Export",
    exportTmx: "Export translation memory (TMX)",
    exportTbx: "Export termbase (TBX)",
    noImportRights: "You can see jobs and export. Importing needs a role that may write translations here.",
    imports: "Imports",
    exports: "Exports",
    show: "Show jobs of",
    scopeProject: "This project",
    scopeTenant: "Whole workspace",
    noImports: "No imports yet.",
    noExports: "No exports yet.",
    loadMore: "Load more",
    columns: {
      file: "File",
      format: "Format",
      mode: "Mode",
      state: "State",
      requestedBy: "Requested by",
      when: "When",
      results: "Results",
      written: "Written",
      actions: "Actions",
    },
    format: { xliff: "XLIFF 2.1", json: "JSON", po: "gettext PO", tmx: "TMX 1.4b", tbx: "TBX-Basic" } as Record<string, string>,
    kind: { catalog: "Catalog", tm: "Translation memory", termbase: "Termbase" } as Record<string, string>,
    mode: { dry_run: "Dry run", merge: "Merge", overwrite: "Overwrite" } as Record<string, string>,
    state: {
      awaiting_upload: "Waiting for the file",
      queued: "Queued",
      running: "Running",
      succeeded: "Done",
      failed: "Failed",
      cancelled: "Cancelled",
    } as Record<string, string>,
    // Visible label + a visually hidden suffix, so the accessible name starts with what's shown.
    cancel: "Cancel",
    ofJob: (name: string) => ` ${name}`,
    cancelRequested: "Stopping after the current batch…",
    cancelled: "Job cancelled.",
    download: "Download",
    downloading: "Downloading…",
    downloaded: (name: string) => `Saved ${name}.`,
    expired: "File deleted after the retention period",
    open: "Results",
    counts: (created: number, updated: number, conflict: number, invalid: number) =>
      [`${created} created`, `${updated} updated`, conflict ? `${conflict} conflicts` : "", invalid ? `${invalid} invalid` : ""].filter(Boolean).join(", "),
    wrote: (n: number) => `${n} written`,
    unnamed: "Unnamed file",
    wizard: {
      title: "Import a file",
      lead: "Choose a file, check how it's read, and start with a dry run: it reports what would change without changing anything.",
      back: "Back to import & export",
      stepFile: "File",
      stepOptions: "How to read it",
      stepMode: "What to do",
      drop: "Drop a file here, or choose one.",
      choose: "File to import",
      chooseHint: "XLIFF 2.1, JSON, gettext PO, TMX or TBX.",
      chosen: (name: string, size: string) => `${name} (${size})`,
      format: "Format",
      detected: (f: string) => `Recognized as ${f}. Change it if that's wrong.`,
      undetected: "The format isn't clear from the file; choose it.",
      needsManage: " — needs the manage permission",
      xliffTarget: "Target locale",
      xliffTargetFrom: (l: string) => `The file says ${l} (trgLang). Choose another locale to import its translations as that one.`,
      xliffTargetUnknown: (l: string) => `The file says ${l} (trgLang), which isn't a locale of this project: choose the locale to import its translations as.`,
      xliffNoTrgLang: "The file names no target locale (trgLang): choose the locale its translations are in.",
      xliffChoose: "Choose the locale to import the file's translations as.",
      chooseLocale: "Choose a locale…",
      xliffNoTarget: "The file has no translations and names no target locale, so only its source text is read (that needs the manage permission).",
      xliffIcu: "Units from other tools carry ICU MessageFormat (read them as MF1)",
      jsonLocale: "Locale of the file",
      sourceCatalog: " — source catalog",
      poLocale: "Target locale",
      poFromHeader: (l: string | undefined) => (l ? `From the file's Language header (${l})` : "From the file's Language header"),
      notInProject: (l: string) => `${l} isn't a locale of this project. Add it under Locales first.`,
      blockedSource: " — needs the manage permission",
      blockedScope: " — outside your locales",
      cannotImportLocale: (l: string) => `You can't import ${l}: it's outside the locales you may translate.`,
      cannotImportSource: "A file in the source locale is a source catalog: importing it needs the manage permission.",
      layout: "Flat and nested JSON are both read.",
      syntax: "Message syntax",
      syntaxMf1: "MF1 (ICU MessageFormat)",
      syntaxMf2: "MF2 (MessageFormat 2)",
      namespace: "Namespace",
      namespaceHint: "Every message of the file lands in this namespace.",
      state: "Review state",
      stateHintJson: "For the file's translations. Approving needs the reviewer role for the locale (or a project without required review); otherwise they wait for review.",
      stateHintPo: "For entries without the fuzzy flag (fuzzy ones wait for review). Capped by your review rights, like the file's own states.",
      pluralVariable: "Plural count variable",
      projectKnowledge: (kind: string) => `Imports into this project's ${kind.toLowerCase()}. The whole workspace's is in workspace settings:`,
      mode: "Mode",
      modes: {
        dry_run: "Check every entry and report what a merge would do. Nothing changes.",
        merge: "Add what's missing and update what changed. Never replaces an approved translation, a source that differs or a concept that differs: those are reported as conflicts.",
        overwrite: "Make the stored text match the file: replaces approved translations and changed sources. Needs the manage permission.",
      } as Record<string, string>,
      start: { dry_run: "Start dry run", merge: "Import", overwrite: "Overwrite…" } as Record<string, string>,
      confirmOverwrite: "Overwrite with this file?",
      confirmOverwriteLead: (name: string) =>
        `${name} replaces what's stored, approved translations and changed source text included. Translations are recorded with provenance “import”, so their history shows where they came from. There's no one-step undo.`,
      overwrite: "Overwrite",
      uploading: (pct: number) => `Uploading… ${pct}%`,
      queued: "Uploaded. Waiting for a worker…",
      running: (done: number, total: number) => (total ? `Reading the file: ${done} of ${total} entries` : "Reading the file…"),
      progress: "Import progress",
    },
    results: {
      title: (name: string) => `Import of ${name}`,
      dryRun: "Dry run: nothing changed. This is what importing the file would do.",
      apply: "Apply this import",
      applyHint: "Imports the same file with the same options as a merge. Conflicts stay as they are: approved translations are kept.",
      applyChoose: "Choose the same file again to apply it",
      applyWrongFile: "That isn't the file this dry run checked (its SHA-256 differs).",
      applying: "Applying…",
      reused: "This file was imported before with the same options, so nothing was applied again. These are the earlier import's results.",
      reusedLink: "Open the earlier import",
      failed: (why: string) => `The import failed: ${why}.`,
      cancelled: "The import was cancelled. What it applied before stopping stays applied.",
      summary: "Summary",
      kindColumn: "Kind",
      status: { created: "Created", updated: "Updated", unchanged: "Unchanged", conflict: "Conflict", invalid: "Invalid" } as Record<string, string>,
      kind: { message: "Message", translation: "Translation", tm_unit: "TM unit", concept: "Concept" } as Record<string, string>,
      filterStatus: "Status",
      filterKind: "Kind",
      all: "All",
      items: "Results",
      key: "Key",
      locale: "Locale",
      where: "Where",
      why: "Why",
      at: (line: number, column: number | undefined) => (column ? `line ${line}, column ${column}` : `line ${line}`),
      refTitle: "Where it is in the file, in the format's own terms",
      none: "No results match.",
      requested: (who: string, when: string) => `Requested by ${who}, ${when}`,
      file: (size: string, sha: string) => `${size} · SHA-256 ${sha.slice(0, 12)}…`,
    },
    codes: {
      approved_translation_conflict: "An approved translation differs; the approved one is kept.",
      source_differs: "The project's source text differs from the file's; the project's is kept, and translations made for the file's text aren't applied.",
      concept_differs: "The termbase's concept differs; the stored one is kept.",
      forbidden: "Not allowed for you: outside your locales, or it needs the manage permission.",
      message_not_found: "No such message in this project. Creating messages needs the manage permission.",
      invalid_message_key: "Not a valid message key.",
      structural_qa_failed: "Doesn't match the source's structure (placeholders, plural branches).",
      invalid_file: "The file can't be read here.",
      write_cannot_reject: "New text can't be imported as rejected.",
    } as Record<string, string>,
    failures: {
      invalid_file: "the file is malformed",
      unsupported_file: "the file uses something this format reader doesn't support",
      file_too_large: "the file is too large",
      source_locale_mismatch: "the file's source locale isn't the project's",
      target_locale_mismatch: "the file's translations are in a locale this project doesn't have — import it again and choose the locale to import it as",
      project_not_found: "the project no longer exists",
      upload_expired: "the file wasn't uploaded within 24 hours",
      not_representable: "the format can't express this catalog with these options",
      internal: "something went wrong on the server",
    } as Record<string, string>,
    exportDialog: {
      title: "Export",
      titleTmx: "Export translation memory",
      titleTbx: "Export termbase",
      format: "Format",
      poImportOnly: "PO files can be imported, not exported.",
      projectKnowledge: "Exports this project's own. The whole workspace's is in workspace settings:",
      locales: "Locales",
      localesHintXliff: "One file per target locale (several are zipped). None: the source only.",
      localesHintJson: "One file per locale (several are zipped). None: the source locale.",
      localesHintTmx: "Keep units with these target locales. None: all.",
      sourceLocale: "Source locale",
      anySource: "Any",
      source: " (source)",
      namespaces: "Namespaces",
      namespacesHint: "The project's namespaces. None chosen: every namespace.",
      noNamespaces: "The project has no messages yet.",
      namespaceCount: (n: number) => ` (${n} ${n === 1 ? "message" : "messages"})`,
      states: "Review states",
      statesHint: "Translations in these states are written.",
      stateNames: { draft: "Draft", needs_review: "Needs review", approved: "Approved", rejected: "Rejected" } as Record<string, string>,
      noStates: "Choose at least one review state.",
      layout: "Layout",
      flat: "Flat keys",
      nested: "Nested objects",
      syntax: "Message syntax",
      start: "Export",
      queued: "Queued…",
      running: "Writing the file…",
      ready: (name: string, size: string, written: number) => `${name} is ready (${size}, ${written} written).`,
      failed: (why: string) => `The export failed: ${why}.`,
      cancelledNote: "The export was cancelled.",
    },
  },
  quality: {
    title: "Quality",
    lead:
      "Everything the checks found, grouped by the layer that found it. `glossa check` and the pull " +
      "request read the same run, so a green terminal and a red pull request cannot disagree.",
    nothingChecked: "Nothing has been checked yet.",
    nothingCheckedHint:
      "Run glossa check, or open a pull request with the GitHub app connected. The run it stores is what this page shows.",
    noFindings: "No findings in this run.",
    noMatches: "No findings match these filters.",
    truncated: (n: number) => `Showing the first ${n.toLocaleString()} findings of this run.`,
    reload: "Reload",
    // ── the run ────────────────────────────────────────────────────
    run: "Check run",
    runOption: (ref: string, when: string, conclusion: string) => `${ref} · ${when} · ${conclusion}`,
    runLatest: "Latest run",
    ranAgainst: (ref: string) => `Ran against ${ref}`,
    commit: (sha: string) => `commit ${sha.slice(0, 7)}`,
    policyVersion: (v: number) => (v === 0 ? "the kernel policy" : `policy v${v}`),
    trigger: {
      cli: "from the CLI",
      pull_request: "from a pull request",
      write: "on a write",
      capture: "from a capture",
      api: "from the API",
    } as Record<string, string>,
    conclusion: { success: "Passed", failure: "Failed", neutral: "Not graded", running: "Running…" } as Record<string, string>,
    counts: "What this run stands at now",
    errors: (n: number) => (n === 1 ? "1 error" : `${n.toLocaleString()} errors`),
    warnings: (n: number) => (n === 1 ? "1 warning" : `${n.toLocaleString()} warnings`),
    waivedCount: (n: number) => (n === 1 ? "1 waived" : `${n.toLocaleString()} waived`),
    waivedNote: "Waived findings are counted on their own. They are never part of the errors or the warnings, and they never fail a check.",
    // ── filters ────────────────────────────────────────────────────
    filters: "Filter findings",
    anyLayer: "Every layer",
    anySeverity: "Every severity",
    anyLocale: "Every locale",
    layer: "Layer",
    severity: "Severity",
    locale: "Locale",
    code: "Rule",
    codePlaceholder: "term_forbidden",
    clear: "Clear filters",
    matched: (n: number) => (n === 1 ? "1 finding matches." : `${n.toLocaleString()} findings match.`),
    // ── layers ─────────────────────────────────────────────────────
    layerName: {
      structure: "Structure",
      parity: "Placeholders & markup",
      completeness: "Completeness",
      terminology: "Terminology",
      style: "Style",
      length: "Length",
      locale: "Locale conventions",
      source: "Source copy",
      visual: "Visual",
      linguistic: "Linguistic",
    } as Record<string, string>,
    layerClean: "Checked, nothing found.",
    notChecked: "Not checked",
    notCheckedHint: (layers: string) => `This run never looked at: ${layers}. Nothing is known about them — that is not the same as clean.`,
    layerSummary: (errors: number, warnings: number, waived: number) =>
      [errors ? `${errors} error${errors === 1 ? "" : "s"}` : "", warnings ? `${warnings} warning${warnings === 1 ? "" : "s"}` : "", waived ? `${waived} waived` : ""]
        .filter(Boolean)
        .join(" · ") || "Nothing found",
    // ── a finding ──────────────────────────────────────────────────
    severityName: { error: "Error", warning: "Warning", waived: "Waived" } as Record<string, string>,
    inLocale: (code: string) => `in ${code}`,
    fixHint: (hint: string) => `Suggested: ${hint}`,
    fixShorten: (to: number) => `Suggested: shorten to ${to} characters.`,
    fixKind: {
      replace: "A replacement is suggested.",
      shorten: "Shortening is suggested.",
      "use-term": "The termbase's preferred term is suggested.",
      "adopt-source-change": "Adopting the source change is suggested.",
    } as Record<string, string>,
    // ── the crop of a visual finding ───────────────────────────────
    onScreen: "On screen",
    cropAlt: (route: string, locale: string) => `Screenshot of ${route} in ${locale}, the message outlined and its surroundings dimmed`,
    cropCaption: (route: string, width: number, height: number, locale: string) => `${route} · ${width}×${height} · ${locale}`,
    cropGone: "The capture this finding is about is not in the current builds any more, so there is nothing to show.",
    cropLoading: "Loading the screenshot…",
    // ── waivers ────────────────────────────────────────────────────
    waive: "Accept this finding…",
    waiveFor: (code: string) => ` (${code})`,
    waiveTitle: "Accept this finding",
    waiveLead:
      "A waiver never hides anything. The finding is still computed, still listed and counted on its own — " +
      "it just stops failing the check, and it comes back on its own when the source it was made against changes.",
    reason: "Why is this finding fine?",
    reasonHint: "Required. The next person to look at this waiver reads only this.",
    reasonPlaceholder: "“Login” is the German term our brand guide prescribes.",
    reasonRequired: "Say why. A waiver without a reason is a suppression nobody has to justify.",
    scope: "How far it reaches",
    scopeProject: "Everywhere in this project",
    scopeBranch: "On one branch only",
    branchRef: "Branch",
    branchRefRequired: "Name the branch this waiver is for.",
    expires: "Expires on (optional)",
    expiresHint: "A daily sweep retires it then. Leave it empty and it stands until someone takes it back.",
    expiresInThePast: "Pick a date in the future.",
    accept: "Accept the finding",
    accepting: "Accepting…",
    waived: "Waived",
    waivedBecause: (reason: string) => `Waived: ${reason}`,
    waivedBy: (who: string, when: string) => `by ${who}, ${when}`,
    waivedScopeBranch: (ref: string) => `on ${ref} only`,
    waivedExpires: (when: string) => `expires ${when}`,
    waivedUnknown: "Waived. The waiver is not on this page.",
    revoke: "Take the waiver back",
    revokeFor: (what: string) => ` for ${what}`,
    accepted: (code: string) => `${code} is accepted, and still listed.`,
    revoked: (code: string) => `The waiver for ${code} is taken back. It is an ordinary finding again.`,
    // ── the waiver list ────────────────────────────────────────────
    waiversTitle: "Waivers",
    waiversLead: "What this project accepts, and why. Revoking one never deletes anything: the waiver stays as history.",
    noWaivers: "This project accepts nothing yet.",
    waiverAccepts: (layer: string, code: string) => `${layer} · ${code}`,
    waiverUnexamined: "No stored finding carries this fingerprint any more.",
    waiverActive: "Stands",
    waiverRevoked: "Revoked",
    waiverExpired: "Expired",
    waiverColumns: {
      accepts: "Accepts",
      reason: "Reason",
      reach: "Reach",
      made: "Made",
      state: "State",
      actions: "Actions",
    },
    readOnly: "You can read this project's findings, but accepting one needs the developer role.",
    // ── the quality sections ───────────────────────────────────────
    sections: "Quality sections",
    sectionFindings: "Findings",
    sectionPolicy: "Check policy",
    sectionWaivers: "Waivers",
    manageWaivers: "Manage waivers",
    // ── the waiver screen (RFC 0005 §9) ────────────────────────────
    waiversPageLead:
      "Every waiver this project has ever made, standing or not. A waiver is the only way a finding stops failing a check, " +
      "so this list is the whole of what the project has agreed to live with — and why.",
    waiverStanding: (n: number) => (n === 1 ? "1 standing" : `${n.toLocaleString()} standing`),
    waiverPast: (n: number) => (n === 1 ? "1 revoked or expired" : `${n.toLocaleString()} revoked or expired`),
    waiverUnexaminedCount: (n: number) =>
      n === 1 ? "1 accepts nothing that is still found" : `${n.toLocaleString()} accept nothing that is still found`,
    waiverExpiringCount: (n: number) => (n === 1 ? "1 expires within 30 days" : `${n.toLocaleString()} expire within 30 days`),
    waiverFilters: "Filter waivers",
    waiverState: "State",
    waiverStateAny: "Standing and past",
    waiverStateActive: "Standing only",
    waiverStateInactive: "Revoked or expired only",
    waiverFingerprint: "Fingerprint",
    waiverFingerprintPlaceholder: "f_7c1a3e9b40d2f815",
    waiverMatched: (n: number) => (n === 1 ? "1 waiver matches." : `${n.toLocaleString()} waivers match.`),
    waiverNoMatches: "No waiver matches these filters.",
    waiverTruncated: (n: number) => `Showing the first ${n.toLocaleString()} waivers.`,
    waiversReadOnly: "You can read this project's waivers, but taking one back needs the developer role.",
  },
  /**
   * The check-policy editor (RFC 0005 §4). Two ideas carry the whole
   * screen and every string below serves one of them: **order is
   * meaning** — the rule naming more fields wins and ties go to the
   * later rule — and **nobody saves blind**: the impact preview is
   * answered before the save, never after it.
   */
  policy: {
    title: "Check policy",
    lead:
      "What `glossa check` and the pull-request check decide by. The server is the source of truth: a developer can tighten " +
      "or loosen their own loop with glossa.yaml, and the pull-request check ignores it.",
    loading: "Reading the policy…",
    version: (v: number) => (v === 0 ? "No policy saved yet — the built-in default grades" : `Version ${v}`),
    versionNever: "Every locale must be complete, an untranslated key in one is an error, and errors fail the check.",
    savedBy: (who: string, when: string) => `Saved by ${who}, ${when}`,
    graceUntil: (when: string, pinned: number) =>
      `Pull requests opened before this version keep grading against version ${pinned} until ${when}.`,
    graceNone: "This version grades every check at once. No pull request is pinned to an older one.",
    readOnly: "You can read this policy, but changing it needs the developer role.",
    // ── the base fields ────────────────────────────────────────────
    baseTitle: "What the check requires",
    baseLead: "The three fields every project starts from. A policy with no rules and no environments decides exactly what these say.",
    requireComplete: "Locales that must be complete",
    requirement: {
      all: "Every locale the project has",
      listed: "Only the locales listed below",
      none: "No locale has to be complete",
    } as Record<string, string>,
    requireCompleteLocales: "The locales that must be complete",
    requireCompleteEmpty: "Pick at least one locale, or choose “No locale has to be complete”.",
    failOn: "The lowest severity that fails a check",
    failOnName: { error: "Errors fail", warning: "Warnings fail too", never: "Nothing fails — the check only reports" } as Record<string, string>,
    missingTranslations: "An untranslated key in a locale that must be complete",
    missingName: { error: "Is an error", warning: "Is a warning" } as Record<string, string>,
    missingHint:
      "Teams that translate after merging set this to a warning and keep every locale required: the check still lists what is untranslated without blocking the pull request.",
    // ── rules ──────────────────────────────────────────────────────
    rulesTitle: "Rules, in the order they are read",
    rulesLead:
      "A rule selects on layer, code, locale, namespace and environment — any subset — and says what those findings are worth. " +
      "The rule naming more fields wins. When two rules name the same number of fields and can both match one finding, the later one wins, " +
      "which is why this list is ordered and reorderable rather than a set.",
    noRules: "No rules. Every finding stands at the severity its layer gave it.",
    addRule: "Add a rule",
    ruleAdded: (n: number, total: number) => `A rule was added at position ${n} of ${total}.`,
    ruleHeading: (n: number, total: number) => `Rule ${n} of ${total}`,
    ruleSelects: "Selects",
    ruleSelectsEverything: "every finding",
    ruleField: { layer: "Layer", code: "Rule code", locale: "Locale", namespace: "Namespace", environment: "Environment" } as Record<string, string>,
    ruleFieldAny: "Any",
    ruleFieldNone: "—",
    ruleCodePlaceholder: "term_forbidden",
    ruleNamespacePlaceholder: "legal",
    ruleEnvironmentPlaceholder: "production",
    ruleSeverity: "Worth",
    ruleSeverityName: {
      error: "Error — fails the check",
      warning: "Warning — reported, does not fail",
      off: "Off — not computed at all",
    } as Record<string, string>,
    ruleOffHint: "“Off” means the layer is not run for what this rule selects, so nothing is reported or counted — and the project does not pay for QA it ignores.",
    ruleMode: "Rollout",
    ruleModeName: {
      enforce: "Enforce — may fail a check",
      warn: "Warn only — reports, never fails a check",
    } as Record<string, string>,
    ruleModeWarnHint:
      "Warn is the on-ramp for a stricter rule: it computes and reports at this severity but cannot change any verdict. Ship it, watch the number, then flip it to enforce.",
    ruleAdvisoryError:
      "The linguistic layer is decided by a model, so no rule may raise it to an error — a build never fails on an opinion. The server refuses this rule.",
    // ── order, said in words ───────────────────────────────────────
    specificity: (named: number) =>
      named === 0 ? "Names no field, so it is the least specific rule there is." : named === 1 ? "Names 1 field." : `Names ${named} fields.`,
    orderFree: "Nothing else here is equally specific and overlapping, so this rule's position does not change any verdict.",
    orderBeatenBy: (n: number) =>
      `Rule ${n} is equally specific, can match the same findings and comes later, so rule ${n} decides them instead of this one.`,
    orderBeats: (list: string) => `It is equally specific to ${list} and comes later, so it decides against ${list === "rule 1" ? "it" : "them"}.`,
    orderShadowed: (n: number) => `Rule ${n} has exactly this selector and comes later, so this rule decides nothing at all. Delete it or move it after rule ${n}.`,
    ruleList: (ns: number[]) => ns.map((n) => `rule ${n}`).join(", "),
    moveUp: "Move up",
    moveDown: "Move down",
    moveUpFor: (n: number) => ` rule ${n}`,
    moveDownFor: (n: number) => ` rule ${n}`,
    moved: (from: number, to: number, total: number) => `Moved from position ${from} to position ${to} of ${total}.`,
    removeRule: "Remove",
    removeRuleFor: (n: number) => ` rule ${n}`,
    ruleRemoved: (n: number) => `Rule ${n} was removed.`,
    // ── environments ───────────────────────────────────────────────
    environmentsTitle: "Environments",
    environmentsLead:
      "What one environment asks for beyond the base. Release enforces the completeness requirement at publish; an environment that names none inherits the document's.",
    noEnvironments: "No environment asks for anything beyond the base.",
    addEnvironment: "Add an environment",
    environmentName: "Name",
    environmentNamePlaceholder: "production",
    environmentNameRequired: "Name the environment.",
    environmentDuplicate: "Two blocks cannot name the same environment.",
    environmentInherit: "Inherit the base requirement",
    environmentReview: "A release must be approved to publish here",
    removeEnvironment: "Remove",
    removeEnvironmentFor: (name: string) => ` the ${name || "unnamed"} environment`,
    // ── the preview, before the save ───────────────────────────────
    previewTitle: "What this change would do",
    previewLead:
      "Measured against the findings this project already has, before anything is stored. This is the whole point of the button: " +
      "nobody should discover a policy change by way of forty red pull requests.",
    preview: "Preview the impact",
    previewing: "Measuring…",
    previewNeeded: "Preview the impact first. The numbers below are what the save would do.",
    previewStale: "The policy changed since this preview. Preview it again before saving.",
    previewUnchanged: "This is the policy that already grades. There is nothing to save.",
    previewMeasured: (findings: number, runs: number) =>
      `Measured against ${findings === 1 ? "1 stored finding" : `${findings.toLocaleString()} stored findings`} from ${runs === 1 ? "1 run" : `${runs.toLocaleString()} runs`} — the newest run of each ref checked recently.`,
    previewNothingToMeasure: "This project has no stored findings yet, so there is nothing to measure this against. The policy still saves.",
    impactRaised: "Findings that would count for more",
    impactLowered: "Findings that would count for less",
    impactSilenced: "Findings that would stop being computed",
    impactNewlyFailing: "Findings that would newly fail a check",
    impactNoLongerFailing: "Findings that would stop failing a check",
    impactOpenPullRequests: "Open pull requests that would newly fail",
    impactNone: "Nothing changes.",
    impactOpenPullRequestsHint:
      "These are the people who would wake up to a red pull request they did not cause. A grace pins them to the version they opened under.",
    impactRefs: (refs: string) => `Would newly fail: ${refs}`,
    impactOtherRefs: (refs: string) => `Other branches that would newly fail: ${refs}`,
    impactPullRequestsLabel: "The open pull requests that would newly fail",
    impactPullRequest: (n: number, ref: string) => `#${n} ${ref}`,
    impactRefsFixed: (refs: string) => `Would stop failing: ${refs}`,
    impactRulesTitle: "Per rule",
    impactRulesLead: "A rule that changed nothing is listed too: that is exactly what a reader wants to know before saving.",
    impactColumns: { rule: "Rule", selector: "Selects", matched: "Decided", changed: "Changed", failing: "Newly failing" },
    impactRuleUnchanged: "Changed nothing",
    // ── the save ───────────────────────────────────────────────────
    saveTitle: "Save it",
    save: (v: number) => `Save version ${v}`,
    saving: "Saving…",
    saved: (v: number) => `Version ${v} is the policy now.`,
    graceDays: "Pin open pull requests for",
    graceDaysUnit: "days",
    graceDaysHint:
      "A pull request opened before this save keeps grading against the version it was opened under for this long. 14 days is the default, because it should protect the people who did not cause the change. 0 pins nothing, which is what a policy that only loosens wants.",
    graceDaysRange: "Between 0 and 90 days.",
    discard: "Discard my changes",
    discarded: "Your changes are discarded. This is the policy that grades.",
    unsaved: "You have unsaved changes.",
    // ── history ────────────────────────────────────────────────────
    historyTitle: "How this policy got to be what it is",
    historyLead: "Every save, newest first. “Who tightened terminology to an error, and when?” has an answer that outlives the version that carried it.",
    noHistory: "No version has been saved through this screen yet.",
    historyColumns: { version: "Version", document: "What it said", by: "Saved by", when: "When", actions: "Actions" },
    historyGrace: (when: string) => `pinned until ${when}`,
    historyLoad: "Load into the editor",
    historyLoadFor: (v: number) => ` version ${v}`,
    historyLoaded: (v: number) => `Version ${v} is loaded in the editor. Nothing is saved until you preview and save it.`,
    summary: (requirement: string, failOn: string, rules: number) =>
      `${requirement} · ${failOn} · ${rules === 1 ? "1 rule" : `${rules.toLocaleString()} rules`}`,
  },
  /**
   * The health surfaces (RFC 0005 §8). Every "none" string below is a
   * sentence rather than a zero, because the difference between "we
   * looked and it is fine" and "nobody looked" is the only thing a
   * health screen is for.
   */
  health: {
    title: "Project health",
    lead:
      "Seven numbers, for the project and for every locale. A number nobody has measured says so rather than " +
      "showing a zero, and a check that cannot run for a language never reads as one that passed.",
    notMeasured: "Not measured",
    unreported: "This server does not report the quality summary yet, so none of these numbers are known.",
    unreadable: "The quality summary could not be read, so none of these numbers are known. They are unknown, not zero.",
    loading: "Measuring…",
    computed: (when: string) => `Measured ${when}`,
    reload: "Measure again",
    // ── the seven numbers (§8's table, in its order) ────────────────
    coverage: "Coverage",
    coverageDetail: (translated: number, outdated: number, missing: number) =>
      `${translated.toLocaleString()} translated · ${outdated.toLocaleString()} outdated · ${missing.toLocaleString()} missing`,
    coverageNone: "There are no active messages to cover yet.",
    findings: "Open findings",
    findingsValue: (errors: number, warnings: number) =>
      `${errors === 1 ? "1 error" : `${errors.toLocaleString()} errors`}, ${warnings === 1 ? "1 warning" : `${warnings.toLocaleString()} warnings`}`,
    findingsDetail: (waived: number, run: string) =>
      `${waived === 1 ? "1 waived" : `${waived.toLocaleString()} waived`}, counted on its own · from the run on ${run}`,
    neverChecked: "Never checked",
    neverCheckedDetail: "Nothing has been checked yet, so no layer here is known to be clean.",
    ai: "AI acceptance",
    aiDetail: (decisions: number, distance: string) =>
      `${decisions === 1 ? "1 decision" : `${decisions.toLocaleString()} decisions`} · mean edit distance ${distance}`,
    aiNone: "No AI suggestion has been accepted, edited or rejected yet.",
    queue: "Review queue",
    queueDepth: (n: number) => (n === 1 ? "1 waiting" : `${n.toLocaleString()} waiting`),
    queueEmpty: "Nothing waiting",
    queueAge: (p50: string, p90: string) => `Half have waited under ${p50}, nine in ten under ${p90}.`,
    queueAgeNone: "Nothing is waiting, so there is no age to report.",
    context: "Context coverage",
    contextValue: (usage: string) => `${usage} have a usage`,
    contextDetail: (region: string, messages: number) =>
      `${region} also have a visible region · ${messages.toLocaleString()} active messages`,
    contextNone: "No build has been uploaded, so nothing is known about where messages appear.",
    leadTime: "Lead time",
    leadTimeValue: (p50: string) => `Half within ${p50}`,
    leadTimeDetail: (p90: string, samples: number) =>
      `Nine in ten within ${p90} · over ${samples.toLocaleString()} published translations`,
    leadTimeNone: "Nothing has gone from a source change to a published translation yet.",
    checks: "Check health",
    checksValue: (rate: string) => `${rate} of checks pass`,
    checksDetail: (runs: number, median: string) =>
      `${runs === 1 ? "1 run" : `${runs.toLocaleString()} runs`} · median ${median} to a conclusion`,
    checksDetailNoMedian: (runs: number) => (runs === 1 ? "1 run" : `${runs.toLocaleString()} runs`),
    checksNone: "No check has run against a pull request yet.",
    // ── the per-locale row ──────────────────────────────────────────
    localesTitle: "Per locale",
    localesLead: "The same numbers for each language, and which checks can run for it at all.",
    localesNone: "This project has no locales yet.",
    localeSource: "Source",
    localeHeading: (code: string) => `Locale ${code}`,
    errorsValue: (n: number) => (n === 1 ? "1 open error" : `${n.toLocaleString()} open errors`),
    errorsNone: "No open errors",
    warningsValue: (n: number) => (n === 1 ? "1 warning" : `${n.toLocaleString()} warnings`),
    queueAgeLabel: "Queue age",
    queueAgeValue: (p50: string) => `Half wait under ${p50}`,
    // ── layer availability (intent §41) ─────────────────────────────
    layersTitle: "Checks that can run here",
    layersSummary: (available: number, total: number) =>
      available === total
        ? `All ${total} checks can run for this locale.`
        : `${available} of ${total} checks can run for this locale. The rest are listed as not available, which is not the same as clean.`,
    layersLegend:
      "“Clean” means the check ran here and found nothing. “Not available” means it cannot run for this language at all, " +
      "and “not checked” means it could have run and this run did not — neither of those is a pass.",
    layerName: (name: string, state: string) => `${name}: ${state}`,
    layerState: {
      clean: "Clean",
      "not-checked": "Not checked",
      unavailable: "Not available",
      unknown: "Not reported",
    } as Record<string, string>,
    layerErrors: (n: number) => (n === 1 ? "1 error" : `${n.toLocaleString()} errors`),
    layerWarnings: (n: number) => (n === 1 ? "1 warning" : `${n.toLocaleString()} warnings`),
    layerUnavailable: {
      unsupported_locale: "No rules or data exist for this language.",
      not_configured: "Nothing is set up for it yet.",
      no_evidence: "It needs evidence this locale has none of.",
    } as Record<string, string>,
    layerUnknown: "The summary did not mention this check, so nothing is known about it.",
    layerNotChecked: "It can run here. The newest run did not run it.",
    // ── the project navigation's one number ─────────────────────────
    navErrors: (n: number) => (n === 1 ? "1 open error" : `${n.toLocaleString()} open errors`),
  },
  work: {
    title: "My work",
    lead: "The translation units given to you — directly, through a role or group you hold, or through your vendor — one batch per assignment. Open one to work through its units in the editor; marking it complete tells the project's workflow it is ready.",
    loading: "Loading your work…",
    failed: "Your work could not be read, so nothing below is known — not even whether there is any.",
    empty: "Nothing is assigned to you. Work given to you, a role or group you hold, or your vendor appears here.",
    live: "To do",
    finished: "Finished",
    noneLive: "Nothing left to do. Finished assignments stay listed below.",
    count: (n: number) => `${n.toLocaleString()} assignment${n === 1 ? "" : "s"}`,
    state: {
      open: "Open",
      accepted: "Accepted",
      done: "Done",
      declined: "Declined",
      expired: "Expired",
    } as Record<string, string>,
    stateTone: {
      open: "neutral",
      accepted: "warn",
      done: "ok",
      declined: "neutral",
      expired: "err",
    } as Record<string, string>,
    unknownProject: (id: string) => `Project ${id.slice(0, 8)}`,
    units: (n: number) => `${n.toLocaleString()} unit${n === 1 ? "" : "s"}`,
    locales: (list: string) => `In ${list}`,
    due: (when: string) => `Due ${when}`,
    overdue: (when: string) => `Overdue since ${when}`,
    noDue: "No due date",
    closed: (when: string) => `Closed ${when}`,
    reason: (r: string) => `Reason: ${r}`,
    open: "Open in the editor",
    accept: "Accept",
    complete: "Mark complete",
    decline: "Decline",
    declineTitle: "Hand this assignment back?",
    declineLead: "The units go back to whoever assigned them. Say why if it helps them give the work to someone else.",
    declineReason: "Reason (optional)",
    declineConfirm: "Decline assignment",
    accepted: (project: string) => `Accepted: the assignment in ${project} is yours to work on.`,
    completed: (project: string) => `Marked complete: the assignment in ${project} is with the project's workflow now.`,
    declined: (project: string) => `Declined: the assignment in ${project} went back.`,
    gone: "Not found: this assignment no longer exists or is no longer given to you. The list has been read again.",
    moved: "Already moved on: this assignment was completed, declined or expired meanwhile. The list has been read again.",
    forbidden: "Not allowed: taking this on needs translating rights in every locale it covers.",
    retry: "Read again",
    // The workspace, opened on one assignment's units.
    scoped: (units: number, locales: string) => `Working on an assignment: ${units.toLocaleString()} unit${units === 1 ? "" : "s"} in ${locales}`,
    scopeLoading: "Loading the assignment…",
    scopeFailed: "The assignment could not be read, so its units can't be shown.",
    showAll: "Show every message",
    back: "Back to My work",
    // The review queue's filter (§3.1), where a workflow is bound.
    assignedToMe: "Assigned to me",
    assignedHint: "Only suggestions for units in your open or accepted assignments.",
    assignedLoading: "Loading your assignments…",
    assignedFailed: "Your assignments could not be read, so the queue can't be narrowed to them.",
    assignedEmpty: "Nothing to review in your assignments.",
    resolutionFailed: "Whether a workflow applies here could not be read, so the “assigned to me” filter is not offered.",
  },
  approvals: {
    title: "Approvals",
    lead: "Translations waiting for your sign-off. Approving is a person's decision: four-eyes keeps authors from approving their own text, and no API token or agent can approve at all.",
    loading: "Loading approvals…",
    failed: "The approvals could not be read, so nothing below is known — not even whether any are waiting.",
    empty: "Nothing is waiting for your approval.",
    count: (n: number) => `${n.toLocaleString()} waiting`,
    pending: "Pending",
    progress: (granted: number, required: number) => `${granted.toLocaleString()} of ${required.toLocaleString()} approval${required === 1 ? "" : "s"} given`,
    askedOfYou: "Asked of you",
    askedOfRole: (role: string) => `Asked of every ${role}`,
    askedOfGroup: (name: string) => `Asked of the group ${name}`,
    groupsFailed: "Your groups could not be read, so approvals asked of a group are listed in case you are in it; the server checks.",
    fourEyes: "Four-eyes: the author of this text can't approve it.",
    ownText: "You wrote this text, so someone else must approve it.",
    text: "Text under approval",
    author: (who: string) => `Written by ${who}`,
    noText: "This unit has no text yet: there is nothing under approval to read.",
    textFailed: "The text under approval could not be read. Don't decide on text you can't see.",
    textLoading: "Loading the text…",
    unknownKey: (id: string) => `Message ${id.slice(0, 8)}`,
    due: (when: string) => `Due ${when}`,
    reason: "Reason (optional)",
    grant: "Approve",
    deny: "Deny",
    granted: (key: string, locale: string) => `Approved ${key} (${locale}).`,
    denied: (key: string, locale: string) => `Denied ${key} (${locale}).`,
    closed: "Already decided: someone granted or denied this approval meanwhile. The list has been read again.",
    superseded: "Replaced: a newer approval request for this text took its place. The list has been read again.",
    retry: "Read again",
  },
  // RFC 0006 wave 5: the workflow editor and instances, vendors and groups, release requests and rollouts.
  workflows: workflowStrings,
  instances: instanceStrings,
  directory: directoryStrings,
  audit: auditStrings,
  releaseOps: releaseOpsStrings,
  shortcuts: {
    title: "Keyboard shortcuts",
    lead: "Studio is built for the keyboard. Chords use ⌘ on macOS and Ctrl elsewhere.",
  },
  problems: {
    network_error: "The server could not be reached. Check your connection and try again.",
    unexpected_response: "The server sent an unexpected response.",
    invalid_response: "The server's response didn't match what Studio expects.",
    unauthenticated: "Your session has ended. Sign in again.",
    invalid_origin:
      "An origin is a scheme, host and port, like https://preview.example.com. Plain http works on localhost only.",
    invalid_origin_label: "A label can be at most 100 characters.",
    origin_registered: "That origin is already on the list.",
    too_many_preview_origins: "A project keeps at most 20 preview origins. Remove one first.",
    origin_not_registered:
      "That origin is not on this project's list, so the editor cannot run there. Add it in project settings first.",
    origin_not_bound: "This editing session was opened for another site. Open the editor again.",
    grant_project_mismatch: "This editing session was opened for another project.",
    no_in_context_permissions: "You cannot edit this project's text, so there is nothing to allow.",
    person_grant_only: "Only a signed-in person can allow in-product editing, not an API token.",
    invalid_credentials: "That email and password don't match.",
    totp_required: "Enter the code from your authenticator app.",
    totp_invalid: "That code isn't valid. Codes change every 30 seconds.",
    email_unverified: "Verify your email first: open the link we sent you.",
    account_locked: "Too many attempts. Password sign-in is locked for 15 minutes; a sign-in link still works.",
    link_invalid: "This link is invalid, expired or already used.",
    weak_password: "Choose a longer password (at least 12 characters).",
    passkeys_disabled: "Passkeys aren't enabled on this server.",
    no_passkeys: "There's no passkey for this address. Use a sign-in link instead.",
    passkey_invalid: "The passkey couldn't be verified.",
    rate_limited: "Too many requests. Wait a moment and try again.",
    device_authorization_not_found: "This code is unknown, expired, or already used.",
    slug_taken: "That slug is taken.",
    forbidden: "You don't have permission to do that here.",
    review_forbidden: "Approving and rejecting needs the reviewer role for this locale.",
    source_locale: "That's the source locale.",
    locale_in_fallback: "Remove it from the fallback graph first.",
    invalid_locale: "That isn't a valid locale code.",
    invalid_transition: "That review step isn't possible from the current state.",
    precondition_failed: "Someone else changed this meanwhile. Reload and try again.",
    translation_conflict: "Someone else created this translation first. Reload and try again.",
    structural_qa_failed: "The translation doesn't match the source's structure.",
    invalid_message: "The text doesn't parse.",
    release_ineligible: "That release has translations this environment's policy doesn't ship.",
    release_not_found: "That release doesn't exist in this project.",
    no_rollback_target: "This environment hasn't served an earlier release.",
    not_in_history: "This environment never served that release.",
    not_releasable: "There's nothing to release yet: add messages first.",
    storage_unavailable: "Object storage is unavailable right now. Nothing changed; try again in a moment.",
    invalid_policy: "That policy isn't valid: choose at least one review state.",
    invalid_note: "The note is too long (at most 1000 characters).",
    invalid_key_name: "Give the key a name of 1 to 200 characters.",
    key_revoked: "That key is already revoked.",
    environment_exists: "An environment with that name exists already.",
    suggestion_decided: "Someone decided on this suggestion meanwhile.",
    suggestion_outdated: "The source changed since this suggestion was made; it can't be accepted any more.",
    translation_rejected: "The translation was refused; check its QA findings.",
    duplicate_term: "That term is listed twice for the same locale.",
    invalid_term_status: "That term status isn't valid.",
    style_guide_exists: "This scope has a style guide already: edit that one.",
    namespace_needs_project: "A namespace guide needs a project.",
    invalid_style_rule: "A rule needs an ID and a title (unless it switches a broader rule off).",
    provider_name_taken: "A provider with that name exists already.",
    provider_in_use: "A routing policy uses this provider. Change the policy first.",
    invalid_provider: "That provider configuration isn't valid.",
    invalid_routing_policy: "Every route needs a configured provider that allows its model.",
    invalid_settings: "Those settings aren't valid.",
    invalid_review_policy: "Scores must be between 0 and 1, the auto-approve line at or above the recommend line.",
    invalid_namespace_tags: "Those namespace tags aren't valid.",
    auto_approve_ineligible: "Auto-approve needs environments that exist and ship approved translations only.",
    job_not_cancellable: "This job can't be cancelled any more: it has started or finished.",
    too_many_keys: "That asks about too many messages at once; narrow the request.",
    locale_not_found: "That locale isn't in this project.",
    invalid_format: "That format isn't possible here (PO files can be imported, not exported).",
    invalid_mode: "That import mode isn't valid.",
    invalid_options: "Those options don't fit this format.",
    project_required: "Catalog files belong to a project.",
    empty_file: "The file is empty.",
    upload_interrupted: "The upload broke off. Try again.",
    upload_not_expected: "This job has its file already.",
    file_too_large: "The file is larger than this server accepts.",
    export_not_ready: "The export isn't finished yet.",
    file_expired: "The file was deleted after the retention period. Export again.",
    idempotency_key_reused: "That request was already sent with other values. Try again.",
    waiver_reason_required: "Say why this finding is fine. A waiver without a reason is refused.",
    invalid_fingerprint: "That isn't a finding Glossa can identify.",
    invalid_waiver_scope: "A waiver reaches the whole project or one branch.",
    waiver_branch_required: "Name the branch a branch-scoped waiver is for.",
    waiver_expiry_in_the_past: "A waiver cannot expire in the past.",
    invalid_waiver: "That waiver isn't valid.",
    invalid_query: "Those filters aren't valid. Clear them and try again.",
    own_text: "Not yours to approve: you wrote this text, and four-eyes means someone other than its author approves it.",
    not_eligible: "Not yours to approve: this approval was asked of other people, or of this locale's reviewers and you aren't one.",
    person_required: "Only a person can approve: an API token or an agent can't, whatever its scopes.",
    approval_closed: "Already decided: someone granted or denied this approval meanwhile.",
    approval_superseded: "Replaced: a newer approval request for this text took its place.",
    assignment_state: "Already moved on: this assignment was completed, declined or expired meanwhile.",
    invalid_assignment: "That isn't possible for this assignment (a reason is at most 2,000 characters).",
    invalid_approval: "That decision isn't valid (a reason is at most 2,000 characters).",
    ...workflowProblems,
    ...instanceProblems,
    ...directoryProblems,
    ...auditProblems,
    ...releaseOpsProblems,
  } as Record<string, string>,
};

/** A person-readable message for any error, preferring our copy for known codes. */
export function problemText(e: unknown): string {
  if (e instanceof ApiError) {
    const known = strings.problems[e.code];
    if (known && e.code !== "invalid_message") return known;
    return e.message || strings.problems.unexpected_response!;
  }
  return e instanceof Error ? e.message : String(e);
}

/** "person:abc" → a short label; the API names principals, not people, in history. */
export function principalLabel(principal: string, selfId?: string): string {
  const [kind, id] = principal.split(":");
  if (kind === "person" && id && id === selfId) return "you";
  if (kind === "token") return `API token ${id?.slice(0, 8) ?? ""}`;
  if (kind === "person") return `member ${id?.slice(0, 8) ?? ""}`;
  return principal;
}
